import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { analystAPI, tradePackagesAPI, authAPI, rateMyTradeAPI } from '../../utils/api';
import { trackEvent } from '../../utils/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { ShellRail } from '../../shell/AppShell';
import UpsellCard from '../../shell/UpsellCard';
import { CurveIcon, IntelIcon } from '../../shell/icons';
import TradeBuilder from './TradeBuilder';
import { formatPickDisplay } from './RosterPicker';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const ff = "var(--ft-font)";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmtPrice = (cents, digits = 0) => `$${(cents / 100).toFixed(digits)}`;

const Price = ({ analyst, digits = 0, showBadge = false, product = 'trade' }) => {
  const roster = product === 'roster';
  const price = roster ? analyst?.rosterReviewPriceInCents : analyst?.priceInCents;
  const compareAt = roster ? analyst?.rosterReviewCompareAtPriceInCents : analyst?.compareAtPriceInCents;
  const onSale = compareAt && compareAt > price;
  if (!onSale) return <>{fmtPrice(price, digits)}</>;
  return (
    <>
      <s style={{ opacity: 0.45, fontWeight: 400, marginRight: '0.34em', textDecorationThickness: '1px' }}>
        {fmtPrice(compareAt, digits)}
      </s>
      {fmtPrice(price, digits)}
      {showBadge && (
        <span style={{ marginLeft: '0.4em', fontSize: '0.78em', fontWeight: 700, color: 'var(--ft-green-ink)', letterSpacing: '0.01em' }}>
          Limited time
        </span>
      )}
    </>
  );
};

const itemLabel = (item) => {
  if (item.type === 'roster') {
    return item._leagueName ? `Full roster review · ${item._leagueName}` : 'Full roster review';
  }
  if (item.type === 'trade') {
    const giving = (item._tradeData?.giving || []).join(', ');
    const getting = (item._tradeData?.getting || []).join(', ');
    const league = item._tradeData?.leagueName;
    if (giving || getting) return `${giving} → ${getting}${league ? ` · ${league}` : ''}`;
    return 'Trade';
  }
  if (item.type === 'hypothetical') {
    const giving = (item.payload?.giving || []).map((a) => a.name).join(', ');
    const getting = (item.payload?.getting || []).map((a) => a.name).join(', ');
    return giving || getting ? `${giving} → ${getting}` : 'Hypothetical trade';
  }
  if (item.type === 'question') {
    return item.question?.slice(0, 60) + (item.question?.length > 60 ? '...' : '') || 'Question';
  }
  return 'Item';
};

const itemTypeBadge = (type) => ({ trade: 'Trade', hypothetical: 'Hypothetical', question: 'Question', roster: 'Roster' }[type] || type);

const itemLeagueKey = (item) => {
  if (item.type === 'trade') return item._tradeData?.leagueName ? `name:${item._tradeData.leagueName}` : 'none';
  if (item.type === 'hypothetical' || item.type === 'roster') return item.leagueId ? `id:${item.leagueId}` : 'none';
  return 'none';
};

const itemLeagueLabel = (item) => {
  if (item.type === 'trade') return item._tradeData?.leagueName || null;
  if (item.type === 'hypothetical' || item.type === 'roster') return item._leagueName || null;
  return null;
};

const itemLeagueAvatar = (item) => {
  if (item.type === 'trade') return item._tradeData?.leagueAvatar || null;
  if (item.type === 'hypothetical' || item.type === 'roster') return item._leagueAvatar || null;
  return null;
};

const sleeperAvatarSrc = (id) => (id ? `https://sleepercdn.com/avatars/thumbs/${id}` : null);

const THE_ASK_OPTIONS = ['Should I do this trade?', 'How would you counter?', 'Just a gut check', 'Something else'];
const EXECUTED_ASK_OPTIONS = ['Did I win this?', 'What should I have done differently?', 'Just a gut check', 'Something else'];
const TIMELINE_OPTIONS = ['All-in to win now', 'Compete this year', 'Retool on the fly', 'Full rebuild', 'Not sure yet'];

// ─────────────────────────────────────────────────────────────────────────────
// Stripe checkout
// ─────────────────────────────────────────────────────────────────────────────
const CheckoutForm = ({ reviewId, analyst, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setPaying(true);
    setError(null);
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    if (stripeError) { setError(stripeError.message); setPaying(false); return; }
    if (paymentIntent?.status === 'succeeded') {
      try {
        await analystAPI.confirmPayment(reviewId, paymentIntent.id);
        onSuccess();
      } catch {
        setError('Payment succeeded but confirmation failed. Contact support.');
      }
    }
    setPaying(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 24 }}>
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>
      {error && <p style={{ color: 'var(--ft-red)', fontSize: 14, marginBottom: 16 }}>{error}</p>}
      <button
        type="submit"
        disabled={!stripe || paying}
        style={{
          width: '100%', background: 'var(--ft-dark)', color: '#fff', border: 'none',
          borderRadius: 'var(--ft-r-pill)', padding: '17px 40px',
          fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em', cursor: 'pointer',
          transition: 'all 0.2s var(--ft-ease)', fontFamily: ff,
          opacity: !stripe || paying ? 0.45 : 1,
        }}
      >
        {paying ? 'Processing…' : <><Price analyst={analyst} digits={2} /></>}
      </button>
    </form>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Inner tabs
// ─────────────────────────────────────────────────────────────────────────────
const MyTradesTab = ({ user, items, onAddItem, maxReached, sharedSleeper, onSharedSleeper }) => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sleeper-first for unauthenticated users
  const [sleeperInput, setSleeperInput] = useState('');
  const [sleeperUserId, setSleeperUserId] = useState('');
  const [leagues, setLeagues] = useState([]);
  const [leaguesLoading, setLeaguesLoading] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [txLoading, setTxLoading] = useState(false);

  const effectiveSleeper = user?.sleeperUsername || sharedSleeper;

  useEffect(() => {
    if (user) {
      tradePackagesAPI.myTrades()
        .then((r) => setTrades(r.data || []))
        .catch(() => setError('Could not load your trades.'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  // Fetch leagues when we have a Sleeper username (non-authed flow)
  useEffect(() => {
    if (!effectiveSleeper || user) return;
    setLeaguesLoading(true);
    rateMyTradeAPI.getUserLeagues(effectiveSleeper)
      .then((r) => {
        const ud = r.data?.user;
        if (ud?.userId) setSleeperUserId(ud.userId);
        const all = r.data?.leagues || [];
        const norm = all.map((l) => ({
          sleeperLeagueId: l.leagueId || l.sleeperLeagueId,
          name: l.name, season: l.season, avatar: l.avatar,
          isSuperFlex: l.isSuperFlex, numTeams: l.numTeams,
          ppr: l.ppr, tePremium: l.tePremium,
        })).filter((l) => l.sleeperLeagueId);
        const byName = new Map();
        norm.forEach((l) => {
          const k = l.name || l.sleeperLeagueId;
          const ex = byName.get(k);
          if (!ex || String(l.season) > String(ex.season)) byName.set(k, l);
        });
        setLeagues([...byName.values()]);
      })
      .catch(() => {})
      .finally(() => setLeaguesLoading(false));
  }, [effectiveSleeper, user]);

  // Fetch transactions when league is selected (non-authed flow)
  const handleSelectLeague = async (league) => {
    setSelectedLeague(league);
    if (!sleeperUserId) return;
    setTxLoading(true);
    try {
      const r = await rateMyTradeAPI.getLeagueTransactions(league.sleeperLeagueId, sleeperUserId);
      const raw = r.data?.trades || (Array.isArray(r.data) ? r.data : []);
      // Normalize from the getLeagueTransactions shape to the shape MyTradesTab renders
      const normalized = raw.map((tx) => ({
        id: tx.transactionId || tx.id,
        sleeperLeagueId: league.sleeperLeagueId,
        leagueName: tx.leagueName || league.name,
        leagueAvatar: league.avatar || null,
        date: tx.createdAt ? new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null,
        vs: tx.otherOwnerName || null,
        payload: {
          giving: [
            ...(tx.teamAGives?.players || []).map((p) => ({ ...p, sleeperId: p.id, type: 'player' })),
            ...(tx.teamAGives?.picks || []).map((p) => ({ ...p, type: 'pick', name: p.displayName || p.name })),
          ],
          getting: [
            ...(tx.teamBGives?.players || []).map((p) => ({ ...p, sleeperId: p.id, type: 'player' })),
            ...(tx.teamBGives?.picks || []).map((p) => ({ ...p, type: 'pick', name: p.displayName || p.name })),
          ],
          wasExecuted: true,
        },
      }));
      setTrades(normalized);
    } catch (err) {
      setError('Could not load trades for this league.');
    }
    setTxLoading(false);
  };

  const selectedIds = new Set(items.filter((i) => i._sleeperTradeId).map((i) => i._sleeperTradeId));

  const toggle = (trade) => {
    if (selectedIds.has(trade.id)) {
      onAddItem(null, trade.id);
    } else {
      if (maxReached) return;
      onAddItem({
        type: 'hypothetical', leagueId: trade.sleeperLeagueId || undefined,
        _leagueName: trade.leagueName, _leagueAvatar: trade.leagueAvatar || null,
        _sleeperTradeId: trade.id, _tradeData: trade,
        payload: { giving: trade.payload?.giving || [], getting: trade.payload?.getting || [], wasExecuted: true, sleeperTradeId: trade.id, executedAt: trade.date || null },
      });
    }
  };

  // Unauthenticated: Sleeper username form
  if (!user && !effectiveSleeper) {
    const handleConnect = (e) => {
      e.preventDefault();
      const clean = sleeperInput.trim();
      if (clean) onSharedSleeper(clean);
    };
    return (
      <div style={{ padding: '4px 0' }}>
        <p style={{ fontSize: 14, color: 'var(--ft-ink-2)', lineHeight: 1.5, margin: '0 0 16px' }}>
          Enter your Sleeper username to pull your trade history.
        </p>
        <form onSubmit={handleConnect} style={{ display: 'flex', gap: 8 }}>
          <input
            type="text" value={sleeperInput}
            onChange={(e) => setSleeperInput(e.target.value)}
            placeholder="sleeper username"
            style={{ flex: 1, background: 'var(--ft-bg)', border: '1px solid var(--ft-hairline)', borderRadius: 10, padding: '12px 14px', fontSize: 15, color: 'var(--ft-ink)', fontFamily: ff, outline: 'none' }}
          />
          <button type="submit" disabled={!sleeperInput.trim()} style={{ background: 'var(--ft-dark)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 600, cursor: !sleeperInput.trim() ? 'not-allowed' : 'pointer', opacity: !sleeperInput.trim() ? 0.4 : 1, fontFamily: ff, whiteSpace: 'nowrap' }}>
            Connect
          </button>
        </form>
      </div>
    );
  }

  // Unauthenticated: league picker
  if (!user && effectiveSleeper && !selectedLeague) {
    if (leaguesLoading) return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
        <div style={{ width: 18, height: 18, border: '2px solid var(--ft-hairline)', borderTopColor: 'var(--ft-ink)', borderRadius: '50%', animation: 'ft-spin 0.7s linear infinite', flexShrink: 0 }} />
        <span style={{ fontSize: 14, color: 'var(--ft-ink-3)' }}>Loading leagues</span>
      </div>
    );
    return (
      <div>
        <p style={{ fontSize: 14, color: 'var(--ft-ink-2)', marginBottom: 16, lineHeight: 1.5 }}>Pick a league.</p>
        {leagues.length === 0 && <p style={{ fontSize: 13, color: 'var(--ft-ink-3)' }}>No dynasty leagues found.</p>}
        {leagues.map((l) => (
          <div
            key={l.sleeperLeagueId} onClick={() => handleSelectLeague(l)}
            className="ft-card"
            style={{ marginBottom: 8, cursor: 'pointer', padding: '12px 16px', border: '1px solid var(--ft-hairline)', display: 'flex', alignItems: 'center', gap: 12 }}
          >
            {l.avatar ? (
              <img src={`https://sleepercdn.com/avatars/thumbs/${l.avatar}`} alt="" style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', flexShrink: 0, background: 'var(--ft-surface)' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--ft-surface)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🏈</div>
            )}
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ft-ink)' }}>{l.name || l.sleeperLeagueId}</div>
              <div style={{ fontSize: 11, color: 'var(--ft-ink-3)', marginTop: 3 }}>
                {[l.numTeams ? `${l.numTeams}tm` : null, l.isSuperFlex ? 'Superflex' : 'Standard', l.ppr === 1 ? 'PPR' : l.ppr === 0.5 ? '0.5 PPR' : 'No PPR', l.tePremium ? `${l.tePremium} TEP` : null].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (loading || txLoading) return <p style={{ color: 'var(--ft-ink-3)', fontSize: 14 }}>Loading trades…</p>;
  if (error) return <p style={{ color: 'var(--ft-red)', fontSize: 13 }}>{error}</p>;
  if (trades.length === 0) {
    return <p style={{ fontSize: 14, color: 'var(--ft-ink-3)', padding: '8px 0' }}>No trades found. Build a hypothetical trade instead.</p>;
  }

  return (
    <div>
      {trades.map((trade) => {
        const received = trade.received || trade.payload?.getting || [];
        const sent = trade.sent || trade.payload?.giving || [];
        const isSelected = selectedIds.has(trade.id);
        const locked = maxReached && !isSelected;
        const txtMain = isSelected ? '#fff' : 'var(--ft-ink)';
        const txtSub = isSelected ? 'rgba(255,255,255,0.6)' : 'var(--ft-ink-3)';
        const lbl = isSelected ? 'rgba(255,255,255,0.35)' : 'var(--ft-ink-3)';
        const chipBg = isSelected ? 'rgba(255,255,255,0.12)' : 'var(--ft-surface)';

        const TradeRow = ({ it, dir }) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            {it.type === 'player' ? (
              <div style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, background: chipBg, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={`https://sleepercdn.com/content/nfl/players/thumb/${it.sleeperId}.jpg`} alt="" style={{ width: 30, height: 30, objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            ) : (
              <div style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, background: chipBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 800, color: txtSub }}>PICK</div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: txtMain, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</div>
              <div style={{ fontSize: 10, color: txtSub, lineHeight: 1.3 }}>
                {it.type === 'player' ? [it.position, it.team].filter(Boolean).join(' • ') : null}
                {it[dir] ? ` · ${dir === 'from' ? 'from' : 'to'} ${it[dir]}` : ''}
              </div>
            </div>
          </div>
        );

        return (
          <div
            key={trade.id}
            onClick={() => !locked && toggle(trade)}
            style={{
              background: isSelected ? 'var(--ft-dark)' : 'var(--ft-bg)',
              border: `1px solid ${isSelected ? 'transparent' : 'var(--ft-hairline)'}`,
              borderRadius: 14, padding: '16px 18px', marginBottom: 8,
              cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.4 : 1,
              transition: 'all 0.2s var(--ft-ease)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              {/* League avatar */}
              {trade.leagueAvatar ? (
                <img
                  src={`https://sleepercdn.com/avatars/${trade.leagueAvatar}`}
                  alt="" onError={(e) => { e.target.style.display = 'none'; }}
                  style={{ width: 22, height: 22, borderRadius: 6, objectFit: 'cover', flexShrink: 0, border: `1px solid ${isSelected ? 'rgba(255,255,255,0.15)' : 'var(--ft-hairline)'}` }}
                />
              ) : (
                <div style={{ width: 22, height: 22, borderRadius: 6, background: isSelected ? 'rgba(255,255,255,0.12)' : 'var(--ft-surface)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: txtSub }}>
                  {(trade.leagueName || 'L')[0]}
                </div>
              )}
              <span style={{ fontSize: 12, fontWeight: 600, color: txtSub, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {trade.leagueName || 'League'}
                <span style={{ fontWeight: 400, color: lbl }}> · {trade.date}{trade.vs ? ` · vs ${trade.vs}` : ''}</span>
              </span>
              {/* Selection indicator */}
              <div style={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                border: `2px solid ${isSelected ? '#fff' : 'var(--ft-disabled)'}`,
                background: isSelected ? '#fff' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isSelected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ft-dark)' }} />}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: lbl, marginBottom: 10 }}>Receives</div>
                {received.length === 0 && <span style={{ fontSize: 12, color: txtSub }}>—</span>}
                {received.map((it, i) => <TradeRow key={i} it={it} dir="from" />)}
              </div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: lbl, marginBottom: 10 }}>Sends</div>
                {sent.length === 0 && <span style={{ fontSize: 12, color: txtSub }}>—</span>}
                {sent.map((it, i) => <TradeRow key={i} it={it} dir="to" />)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const BuildTradeTab = ({ user, onAddItem, maxReached, onLinkSleeper, sharedSleeper, onSharedSleeper }) => {
  const [leagues, setLeagues] = useState([]);
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [allTeams, setAllTeams] = useState([]);
  const [myTeam, setMyTeam] = useState(null);
  const [theirTeam, setTheirTeam] = useState(null);
  const [rostersLoading, setRostersLoading] = useState(false);
  const [draftedSeasons, setDraftedSeasons] = useState([]);
  const [myGives, setMyGives] = useState({ players: [], picks: [] });
  const [theirGives, setTheirGives] = useState({ players: [], picks: [] });
  const [note, setNote] = useState('');
  const [added, setAdded] = useState(false);
  const [sleeperInput, setSleeperInput] = useState('');
  const [sleeperLinking, setSleeperLinking] = useState(false);
  const [sleeperLinkError, setSleeperLinkError] = useState(null);

  const effectiveSleeper = user?.sleeperUsername || sharedSleeper;

  useEffect(() => {
    if (!effectiveSleeper) { setLeaguesLoading(false); return; }
    rateMyTradeAPI.getUserLeagues(effectiveSleeper)
      .then((r) => {
        const all = r.data?.leagues || r.data || [];
        const norm = (Array.isArray(all) ? all : []).map((l) => ({
          sleeperLeagueId: l.leagueId || l.sleeperLeagueId || l.id,
          name: l.name || l.leagueName, season: l.season,
          isSuperFlex: l.isSuperFlex || l.settings?.isSuperFlex || false,
          numTeams: l.numTeams || l.total_rosters,
          avatar: l.avatar || null,
          ppr: l.ppr ?? null,
          tePremium: l.tePremium ?? null,
        })).filter((l) => l.sleeperLeagueId);
        const byName = new Map();
        norm.forEach((l) => {
          const k = l.name || l.sleeperLeagueId;
          const ex = byName.get(k);
          if (!ex || String(l.season) > String(ex.season)) byName.set(k, l);
        });
        setLeagues([...byName.values()]);
      })
      .catch(() => {})
      .finally(() => setLeaguesLoading(false));
  }, [effectiveSleeper]);

  const resetTradeState = () => {
    setMyGives({ players: [], picks: [] }); setTheirGives({ players: [], picks: [] });
    setTheirTeam(null); setNote('');
  };

  const handleSelectLeague = async (league) => {
    setSelectedLeague(league); resetTradeState(); setMyTeam(null); setAllTeams([]); setDraftedSeasons([]);
    setRostersLoading(true);
    try {
      const leagueId = league.sleeperLeagueId || league.id;
      const r = await rateMyTradeAPI.getLeagueRosters(leagueId);
      setDraftedSeasons(r.data?.draftedSeasons || []);
      const teams = r.data?.teams || r.data?.rosters || (Array.isArray(r.data) ? r.data : []);
      setAllTeams(teams);
      const sUid = String(user?.sleeperUserId || '');
      const uname = String(user?.sleeperUsername || sharedSleeper || '').toLowerCase();
      const dname = String(user?.sleeperDisplayName || '').toLowerCase();
      const mine = teams.find((ro) => {
        if (sUid && String(ro.sleeperUserId) === sUid) return true;
        const owner = String(ro.ownerName || '').toLowerCase();
        const team = String(ro.teamName || '').toLowerCase();
        if (uname && (owner === uname || team === uname)) return true;
        if (dname && (owner === dname || team === dname)) return true;
        return false;
      });
      setMyTeam(mine || null);
    } catch (err) { console.error('[BuildTradeTab] getLeagueRosters failed', err); }
    setRostersLoading(false);
  };

  const flatten = (g) => [
    ...g.players.map((p) => ({ name: p.name, sleeperId: p.id, position: p.position, team: p.team })),
    ...g.picks.map((pk) => ({ name: formatPickDisplay(pk), type: 'pick', season: pk.season, round: pk.round, override: pk.override || null })),
  ];

  const totalSelected = myGives.players.length + myGives.picks.length + theirGives.players.length + theirGives.picks.length;

  const handleAdd = () => {
    if (totalSelected === 0) return;
    onAddItem({
      type: 'hypothetical', leagueId: selectedLeague?.sleeperLeagueId || selectedLeague?.id,
      _leagueName: selectedLeague?.name || null, _leagueAvatar: selectedLeague?.avatar || null,
      _counterparty: theirTeam?.ownerName || theirTeam?.teamName || null,
      payload: { giving: flatten(myGives), getting: flatten(theirGives), counterparty: theirTeam?.ownerName || theirTeam?.teamName || null, note: note.trim() || undefined },
    });
    resetTradeState(); setAdded(true); setTimeout(() => setAdded(false), 2000);
  };

  if (!effectiveSleeper) {
    const handleInlineLinkSleeper = async (e) => {
      e.preventDefault();
      const clean = sleeperInput.trim();
      if (!clean) return;
      if (!user) {
        onSharedSleeper(clean);
        return;
      }
      setSleeperLinking(true); setSleeperLinkError(null);
      try {
        await onLinkSleeper(clean);
        // user prop will update via refreshUser() — leagues useEffect will fire
      } catch (err) {
        setSleeperLinkError(err?.response?.data?.error || 'Could not find that Sleeper username.');
      }
      setSleeperLinking(false);
    };

    return (
      <div style={{ padding: '4px 0' }}>
        <p style={{ fontSize: 14, color: 'var(--ft-ink-2)', lineHeight: 1.5, margin: '0 0 16px' }}>
          Enter your Sleeper username to pull in your leagues.
        </p>
        <form onSubmit={handleInlineLinkSleeper} style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={sleeperInput}
            onChange={(e) => { setSleeperInput(e.target.value); setSleeperLinkError(null); }}
            placeholder="sleeper username"
            style={{
              flex: 1, background: 'var(--ft-bg)', border: '1px solid var(--ft-hairline)',
              borderRadius: 10, padding: '12px 14px', fontSize: 15, color: 'var(--ft-ink)',
              fontFamily: ff, outline: 'none',
            }}
          />
          <button
            type="submit"
            disabled={!sleeperInput.trim() || sleeperLinking}
            style={{
              background: 'var(--ft-dark)', color: '#fff', border: 'none',
              borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 600,
              cursor: !sleeperInput.trim() || sleeperLinking ? 'not-allowed' : 'pointer',
              opacity: !sleeperInput.trim() || sleeperLinking ? 0.4 : 1,
              fontFamily: ff, whiteSpace: 'nowrap',
            }}
          >
            {sleeperLinking ? 'Connecting…' : 'Connect'}
          </button>
        </form>
        {sleeperLinkError && (
          <p style={{ fontSize: 13, color: 'var(--ft-red)', margin: '8px 0 0' }}>{sleeperLinkError}</p>
        )}
      </div>
    );
  }
  if (leaguesLoading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
      <div style={{ width: 18, height: 18, border: '2px solid var(--ft-hairline)', borderTopColor: 'var(--ft-ink)', borderRadius: '50%', animation: 'ft-spin 0.7s linear infinite', flexShrink: 0 }} />
      <span style={{ fontSize: 14, color: 'var(--ft-ink-3)' }}>Loading leagues</span>
    </div>
  );

  if (!selectedLeague) {
    return (
      <div>
        <p style={{ fontSize: 14, color: 'var(--ft-ink-2)', marginBottom: 16, lineHeight: 1.5 }}>Pick a league.</p>
        {leagues.length === 0 && <p style={{ fontSize: 13, color: 'var(--ft-ink-3)' }}>No dynasty leagues found.</p>}
        {leagues.map((l) => (
          <div
            key={l.sleeperLeagueId}
            onClick={() => handleSelectLeague(l)}
            className="ft-card"
            style={{ marginBottom: 8, cursor: 'pointer', padding: '12px 16px', border: '1px solid var(--ft-hairline)', display: 'flex', alignItems: 'center', gap: 12 }}
          >
            {l.avatar ? (
              <img
                src={`https://sleepercdn.com/avatars/thumbs/${l.avatar}`}
                alt=""
                style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', flexShrink: 0, background: 'var(--ft-surface)' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--ft-surface)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                🏈
              </div>
            )}
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ft-ink)' }}>{l.name || l.sleeperLeagueId}</div>
              <div style={{ fontSize: 11, color: 'var(--ft-ink-3)', marginTop: 3 }}>
                {[
                  l.numTeams ? `${l.numTeams}tm` : null,
                  l.isSuperFlex ? 'Superflex' : 'Standard',
                  l.ppr === 1 ? 'PPR' : l.ppr === 0.5 ? '0.5 PPR' : 'No PPR',
                  l.tePremium ? `${l.tePremium} TEP` : null,
                ].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const BackToLeague = ({ children }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
      <button
        onClick={() => { setSelectedLeague(null); setMyTeam(null); setAllTeams([]); resetTradeState(); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ft-ink-3)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}
      >
        ← {selectedLeague.name}
      </button>
      {children}
    </div>
  );

  if (rostersLoading) {
    return (
      <div><BackToLeague />
        <p style={{ color: 'var(--ft-ink-3)', fontSize: 14 }}>Loading rosters…</p>
      </div>
    );
  }

  if (!myTeam) {
    return (
      <div><BackToLeague />
        <p style={{ fontSize: 13, color: 'var(--ft-ink-3)' }}>Could not match your roster. Try another league or relink your Sleeper account.</p>
      </div>
    );
  }

  if (!theirTeam) {
    const others = allTeams.filter((t) => t.rosterId !== myTeam.rosterId);
    return (
      <div>
        <BackToLeague />
        <span className="ft-k" style={{ display: 'block', marginBottom: 12 }}>Who are you trading with?</span>
        {others.map((t) => {
          const rec = t.prevRecord || t.record || {};
          const recLabel = t.prevRecord ? `2025: ${rec.wins || 0}-${rec.losses || 0}` : `${rec.wins || 0}-${rec.losses || 0}`;
          return (
            <div
              key={t.rosterId}
              onClick={() => setTheirTeam(t)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                background: 'var(--ft-surface)', borderRadius: 12, padding: '12px 14px',
                marginBottom: 8, cursor: 'pointer', border: '1px solid transparent',
                transition: 'background 0.12s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ft-surface-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--ft-surface)'; }}
            >
              <div style={{ width: 36, height: 36, borderRadius: '50%', overflow: 'hidden', background: 'var(--ft-hairline)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {t.avatar ? (
                  <img
                    src={(typeof t.avatar === 'string' && t.avatar.startsWith('http')) ? t.avatar : `https://sleepercdn.com/avatars/thumbs/${t.avatar}`}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ft-ink-3)' }}>{(t.ownerName || '?').charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ft-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.ownerName}</div>
                <div style={{ fontSize: 12, color: 'var(--ft-ink-3)' }}>{recLabel}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      <BackToLeague>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => { setTheirTeam(null); setMyGives({ players: [], picks: [] }); setTheirGives({ players: [], picks: [] }); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ft-ink-3)', fontSize: 12 }}
        >
          Change opponent
        </button>
      </BackToLeague>

      <TradeBuilder
        myTeam={myTeam} theirTeam={theirTeam}
        myGives={myGives} theirGives={theirGives}
        onChangeMy={setMyGives} onChangeTheir={setTheirGives}
        numTeams={selectedLeague?.numTeams} isSuperFlex={selectedLeague?.isSuperFlex}
        draftedSeasons={draftedSeasons}
      />

      <button
        onClick={handleAdd}
        disabled={maxReached || totalSelected === 0}
        className="ft-btn"
        style={{
          width: '100%', marginTop: 16,
          padding: '14px 24px', fontSize: 15,
          opacity: (maxReached || totalSelected === 0) ? 0.35 : 1,
        }}
      >
        {added ? 'Added ✓' : maxReached ? 'Max 3 items' : 'Add to submission'}
      </button>

      <div style={{ marginTop: 12 }}>
        <textarea
          value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Optional: why you're considering this, what you're trying to fix..."
          style={{
            width: '100%', minHeight: 60, background: 'var(--ft-surface)',
            border: '1px solid var(--ft-hairline)', borderRadius: 12,
            padding: '12px 14px', fontSize: 13, fontFamily: ff, color: 'var(--ft-ink)',
            resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5,
          }}
        />
      </div>
    </div>
  );
};

const AskQuestionTab = ({ onAddItem, maxReached }) => {
  const [question, setQuestion] = useState('');
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    if (!question.trim()) return;
    onAddItem({ type: 'question', question: question.trim() });
    setQuestion(''); setAdded(true); setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div>
      <textarea
        value={question} onChange={(e) => setQuestion(e.target.value)}
        placeholder="How aggressively should I be collecting 2027 1sts in a rebuilding SF league?"
        style={{
          width: '100%', minHeight: 120, background: 'var(--ft-bg)',
          border: '1px solid var(--ft-hairline)', borderRadius: 12,
          padding: 16, fontSize: 15, fontFamily: ff, color: 'var(--ft-ink)',
          resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.6,
          marginBottom: 12,
        }}
        onFocus={(e) => { e.target.style.borderColor = 'var(--ft-dark)'; e.target.style.boxShadow = '0 0 0 3px rgba(0,0,0,0.06)'; }}
        onBlur={(e) => { e.target.style.borderColor = 'var(--ft-hairline)'; e.target.style.boxShadow = 'none'; }}
      />
      <button
        onClick={handleAdd} disabled={maxReached || !question.trim()}
        className="ft-btn"
        style={{ width: '100%', padding: '14px 24px', fontSize: 15, opacity: (maxReached || !question.trim()) ? 0.35 : 1 }}
      >
        {added ? 'Added ✓' : maxReached ? 'Max 3 items' : 'Add to submission'}
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Pick a league (roster review — step 1)
// ─────────────────────────────────────────────────────────────────────────────
const PickLeagueTab = ({ user, sharedSleeper, onSharedSleeper, onPick, selectedLeagueId }) => {
  const [sleeperInput, setSleeperInput] = useState('');
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const effectiveSleeper = user?.sleeperUsername || sharedSleeper;

  useEffect(() => {
    if (!effectiveSleeper) return;
    setLoading(true); setError(null);
    rateMyTradeAPI.getUserLeagues(effectiveSleeper)
      .then((r) => {
        const all = r.data?.leagues || [];
        const norm = all.map((l) => ({
          sleeperLeagueId: l.leagueId || l.sleeperLeagueId,
          name: l.name, season: l.season, avatar: l.avatar,
          isSuperFlex: l.isSuperFlex, numTeams: l.numTeams,
          ppr: l.ppr, tePremium: l.tePremium,
        })).filter((l) => l.sleeperLeagueId);
        // League rollover: keep only the newest season per league name (see CLAUDE.md)
        const byName = new Map();
        norm.forEach((l) => {
          const k = l.name || l.sleeperLeagueId;
          const ex = byName.get(k);
          if (!ex || String(l.season) > String(ex.season)) byName.set(k, l);
        });
        setLeagues([...byName.values()]);
      })
      .catch(() => setError('Could not find that Sleeper account.'))
      .finally(() => setLoading(false));
  }, [effectiveSleeper]);

  if (!effectiveSleeper) {
    return (
      <div>
        <p style={{ fontSize: 14, color: 'var(--ft-ink-2)', marginBottom: 16, lineHeight: 1.5 }}>
          Enter your Sleeper username and we'll pull up your dynasty leagues. Read-only — no password needed.
        </p>
        <form onSubmit={(e) => { e.preventDefault(); if (sleeperInput.trim()) onSharedSleeper(sleeperInput.trim()); }}>
          <input
            placeholder="Sleeper username"
            value={sleeperInput}
            onChange={(e) => setSleeperInput(e.target.value)}
            autoCapitalize="none" autoCorrect="off" spellCheck={false}
            style={{ width: '100%', background: 'var(--ft-surface)', border: '1px solid var(--ft-hairline)', borderRadius: 12, padding: '16px 20px', fontSize: 17, fontFamily: ff, color: 'var(--ft-ink)', outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}
          />
          <button type="submit" disabled={!sleeperInput.trim()} className="ft-btn" style={{ width: '100%', padding: '15px 24px', fontSize: 15, opacity: sleeperInput.trim() ? 1 : 0.35 }}>
            Find my leagues
          </button>
        </form>
      </div>
    );
  }

  if (loading) {
    return <p style={{ fontSize: 14, color: 'var(--ft-ink-3)', padding: '24px 0' }}>Loading your leagues…</p>;
  }
  if (error) {
    return (
      <div>
        <p style={{ color: 'var(--ft-red)', fontSize: 13, marginBottom: 14 }}>{error}</p>
        {!user?.sleeperUsername && (
          <button className="ft-btn ghost sm" onClick={() => onSharedSleeper('')}>Try a different username</button>
        )}
      </div>
    );
  }
  if (leagues.length === 0) {
    return <p style={{ fontSize: 14, color: 'var(--ft-ink-3)', padding: '24px 0' }}>No dynasty leagues found for {effectiveSleeper}.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {leagues.map((l) => {
        const selected = selectedLeagueId === l.sleeperLeagueId;
        return (
          <button
            key={l.sleeperLeagueId}
            onClick={() => onPick(l)}
            style={{
              display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
              padding: '15px 18px', width: '100%', cursor: 'pointer',
              background: selected ? 'var(--ft-dark)' : '#fff',
              border: `1.5px solid ${selected ? 'var(--ft-dark)' : 'var(--ft-hairline)'}`,
              borderRadius: 16, transition: 'all 0.18s var(--ft-ease)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
            onMouseEnter={(e) => { if (!selected) { e.currentTarget.style.borderColor = '#1D1D1F'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
            onMouseLeave={(e) => { if (!selected) { e.currentTarget.style.borderColor = 'var(--ft-hairline)'; e.currentTarget.style.transform = 'none'; } }}
          >
            {l.avatar ? (
              <img src={sleeperAvatarSrc(l.avatar)} alt="" onError={(e) => { e.target.style.display = 'none'; }} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', background: 'var(--ft-surface)', flexShrink: 0 }} />
            ) : (
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: selected ? 'rgba(255,255,255,0.16)' : 'var(--ft-surface)', color: selected ? '#fff' : 'var(--ft-ink-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, flexShrink: 0 }}>
                {(l.name || 'L').charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: selected ? '#fff' : 'var(--ft-ink)', letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</div>
              <div style={{ fontSize: 12, color: selected ? 'rgba(255,255,255,0.65)' : 'var(--ft-ink-3)', marginTop: 2 }}>
                {l.season} · {l.numTeams}-team · {l.isSuperFlex ? 'SF' : '1QB'}{l.tePremium ? ' · TEP' : ''}
              </div>
            </div>
            <span style={{ color: selected ? '#fff' : 'var(--ft-ink-3)', fontSize: 18, flexShrink: 0, fontWeight: 300 }}>{selected ? '✓' : '›'}</span>
          </button>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Progress dots (shown in the wizard anchor row)
// ─────────────────────────────────────────────────────────────────────────────
// Steps shown in topbar progress (auth/sleeper are conditional pre-payment gates)
const WIZARD_STEPS = ['build', 'context', 'payment'];
const ROSTER_WIZARD_STEPS = ['league', 'context', 'payment'];

const WizardProgress = ({ step, steps = WIZARD_STEPS }) => {
  const idx = steps.indexOf(step);
  if (idx < 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {steps.map((s, i) => (
        <div key={s} style={{
          width: 28, height: 3, borderRadius: 2,
          background: i < idx ? 'var(--ft-green)' : i === idx ? 'var(--ft-dark)' : 'var(--ft-hairline)',
          transition: 'all 0.3s ease',
        }} />
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const AnalystLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [analyst, setAnalyst] = useState(null);
  const [loading, setLoading] = useState(true);
  // pbs.twimg.com avatar/banner URLs die whenever the analyst changes their X photo;
  // fall back to the initial-circle / blank banner rather than showing a broken <img>.
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [bannerBroken, setBannerBroken] = useState(false);
  const [step, setStep] = useState(() => {
    try {
      const snap = JSON.parse(localStorage.getItem(`ft_ar_snap_${slug}`));
      if (snap?.ts && Date.now() - snap.ts < 30 * 60 * 1000 && snap.items?.length) {
        const s = snap.step;
        // Restore to wizard step; 'auth' auto-advances once user loads if already authenticated
        if (s === 'auth' || s === 'sleeper' || s === 'context' || s === 'build' || s === 'league') return s;
      }
    } catch {}
    return 'profile';
  });

  // 'trade' | 'roster' — which product the wizard is buying
  const [product, setProduct] = useState(() => {
    try {
      const ss = sessionStorage.getItem(`ft_ar_prod_${slug}`);
      if (ss === 'roster' || ss === 'trade') return ss;
      const snap = JSON.parse(localStorage.getItem(`ft_ar_snap_${slug}`));
      if (snap?.ts && Date.now() - snap.ts < 30 * 60 * 1000 && (snap.product === 'roster' || snap.product === 'trade')) {
        return snap.product;
      }
    } catch {}
    return 'trade';
  });

  // Which product the profile's detail sections (how-it-works, verdict shape)
  // are showing. Only relevant when the analyst offers both.
  const [detailProduct, setDetailProduct] = useState('roster');

  const [email, setEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  const [sleeperInput, setSleeperInput] = useState(() => {
    try {
      const ss = sessionStorage.getItem(`ft_ar_su_${slug}`);
      if (ss) return ss;
      const snap = JSON.parse(localStorage.getItem(`ft_ar_snap_${slug}`));
      if (snap?.ts && Date.now() - snap.ts < 30 * 60 * 1000) return snap.sharedSleeper || '';
    } catch {}
    return '';
  });
  const [sleeperLoading, setSleeperLoading] = useState(false);
  const [sleeperError, setSleeperError] = useState(null);

  const [sharedSleeper, setSharedSleeper] = useState(() => {
    try {
      const ss = sessionStorage.getItem(`ft_ar_su_${slug}`);
      if (ss) return ss;
      const snap = JSON.parse(localStorage.getItem(`ft_ar_snap_${slug}`));
      if (snap?.ts && Date.now() - snap.ts < 30 * 60 * 1000) return snap.sharedSleeper || '';
    } catch {}
    return '';
  });
  const [activeTab, setActiveTab] = useState('build');
  // 'hub' shows the 3 numbered trade slots; 'form' shows the mode picker / active builder.
  const [buildView, setBuildView] = useState('hub');
  const [justAddedIndex, setJustAddedIndex] = useState(null);
  const [addedToast, setAddedToast] = useState(false);
  const toastTimerRef = useRef(null);
  const [items, setItems] = useState(() => {
    try {
      const ss = JSON.parse(sessionStorage.getItem(`ft_ar_items_${slug}`));
      if (ss?.length) return ss;
      // Restore from localStorage only if a magic-link handoff is pending (< 30 min old)
      const snap = JSON.parse(localStorage.getItem(`ft_ar_snap_${slug}`));
      if (snap?.ts && Date.now() - snap.ts < 30 * 60 * 1000) return snap.items || [];
    } catch {}
    return [];
  });

  const [leagueContext, setLeagueContext] = useState(() => {
    try {
      const ss = JSON.parse(sessionStorage.getItem(`ft_ar_lctx_${slug}`));
      if (ss && Object.keys(ss).length) return ss;
      const snap = JSON.parse(localStorage.getItem(`ft_ar_snap_${slug}`));
      if (snap?.ts && Date.now() - snap.ts < 30 * 60 * 1000) return snap.leagueContext || {};
    } catch {}
    return {};
  });
  const [itemContext, setItemContext] = useState(() => {
    try {
      const ss = JSON.parse(sessionStorage.getItem(`ft_ar_ictx_${slug}`));
      if (ss && Object.keys(ss).length) return ss;
      const snap = JSON.parse(localStorage.getItem(`ft_ar_snap_${slug}`));
      if (snap?.ts && Date.now() - snap.ts < 30 * 60 * 1000) return snap.itemContext || {};
    } catch {}
    return {};
  });
  const [contextError, setContextError] = useState(null);
  const [activeLeagueIdx, setActiveLeagueIdx] = useState(0);
  const [openOptionals, setOpenOptionals] = useState({});

  const [paymentError, setPaymentError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 600);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // sessionStorage keeps wizard progress alive within the same tab session
  useEffect(() => {
    try { sessionStorage.setItem(`ft_ar_items_${slug}`, JSON.stringify(items)); } catch {}
  }, [items, slug]);
  useEffect(() => {
    try { sessionStorage.setItem(`ft_ar_lctx_${slug}`, JSON.stringify(leagueContext)); } catch {}
  }, [leagueContext, slug]);
  useEffect(() => {
    try { sessionStorage.setItem(`ft_ar_ictx_${slug}`, JSON.stringify(itemContext)); } catch {}
  }, [itemContext, slug]);
  useEffect(() => {
    try {
      if (sharedSleeper) sessionStorage.setItem(`ft_ar_su_${slug}`, sharedSleeper);
      else sessionStorage.removeItem(`ft_ar_su_${slug}`);
    } catch {}
  }, [sharedSleeper, slug]);
  useEffect(() => {
    try { sessionStorage.setItem(`ft_ar_prod_${slug}`, product); } catch {}
  }, [product, slug]);

  // Clear stale magic-link snap once the user is authenticated (handoff complete)
  useEffect(() => {
    if (user) {
      try { localStorage.removeItem(`ft_ar_snap_${slug}`); } catch {}
    }
  }, [user, slug]);

  useEffect(() => {
    analystAPI.getAnalyst(slug)
      .then((r) => setAnalyst(r.data))
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  // Step flow: profile → build → context → auth (if needed) → sleeper (if needed) → payment
  // After returning from magic link, auto-advance past auth/sleeper if now logged in.
  useEffect(() => {
    if (user && step === 'auth') {
      if (user.sleeperUsername) {
        setStep('payment_loading');
      } else if (sharedSleeper) {
        // Already collected Sleeper username earlier in the wizard — link silently and skip the form
        analystAPI.linkSleeper(sharedSleeper)
          .then(() => refreshUser())
          .then(() => { setStep('payment_loading'); window.scrollTo(0, 0); })
          .catch(() => setStep('sleeper')); // fall back to form if link fails
      } else {
        setStep('sleeper');
      }
    }
    if (user?.sleeperUsername && step === 'sleeper') {
      setStep('payment_loading');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, step]);

  // When we reach payment_loading state, trigger the payment intent creation
  useEffect(() => {
    if (step === 'payment_loading') handleProceedToPayment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleStart = (which = 'trade') => {
    setProduct(which);
    // A stale cart from the other product would fail backend validation —
    // switching products always starts a fresh submission.
    setItems((prev) => {
      const hasOtherProduct = prev.some((i) => (which === 'roster') !== (i.type === 'roster'));
      return hasOtherProduct ? [] : prev;
    });
    setStep(which === 'roster' ? 'league' : 'build');
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    const prev = {
      build: 'profile',
      league: 'profile',
      context: product === 'roster' ? 'league' : 'build',
      auth: 'context',
      sleeper: 'context',
      payment: 'context',
    };
    setStep(prev[step] || 'profile');
    window.scrollTo(0, 0);
  };

  const handleLinkSleeper = async (e) => {
    e.preventDefault();
    const clean = sleeperInput.trim();
    if (!clean) return;
    setSleeperLoading(true); setSleeperError(null);
    try {
      await analystAPI.linkSleeper(clean);
      await refreshUser();
      setStep('payment_loading'); window.scrollTo(0, 0);
    } catch (err) {
      setSleeperError(err?.response?.data?.error || 'Could not link that Sleeper account.');
    }
    setSleeperLoading(false);
  };

  const handleSendMagicLink = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    localStorage.setItem('loginReturnUrl', window.location.pathname);
    // Snapshot wizard state so the magic-link new tab can restore it
    try {
      localStorage.setItem(`ft_ar_snap_${slug}`, JSON.stringify({
        ts: Date.now(),
        step,
        product,
        items,
        leagueContext,
        itemContext,
        sharedSleeper,
      }));
    } catch {}
    try {
      await authAPI.requestMagicLink(email);
      setMagicLinkSent(true);
    } catch {
      localStorage.removeItem('loginReturnUrl');
      try { localStorage.removeItem(`ft_ar_snap_${slug}`); } catch {}
    }
    setAuthLoading(false);
  };

  const handleAddItem = (item, removeTradeId) => {
    if (removeTradeId) {
      setItems((prev) => prev.filter((i) => !(i._sleeperTradeId === removeTradeId || (i.type === 'trade' && i.tradeId === removeTradeId))));
      return;
    }
    if (!item) return;
    const newIndex = items.length;
    setItems((prev) => [...prev, item]);
    // Land back on the trade selector instead of leaving the user inside the
    // nested league/opponent picker with no obvious next step.
    setActiveTab('build');
    setBuildView('hub');
    setJustAddedIndex(newIndex);
    setAddedToast(true);
    window.scrollTo(0, 0);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setAddedToast(false);
      setJustAddedIndex(null);
    }, 2600);
  };

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const maxReached = items.length >= 3;

  const leagueGroups = React.useMemo(() => {
    const map = new Map();
    items.forEach((item, idx) => {
      const key = itemLeagueKey(item);
      if (key === 'none') return;
      if (!map.has(key)) map.set(key, { key, label: itemLeagueLabel(item), avatar: itemLeagueAvatar(item), itemIdxs: [] });
      const g = map.get(key);
      if (!g.label) g.label = itemLeagueLabel(item);
      if (!g.avatar) g.avatar = itemLeagueAvatar(item);
      g.itemIdxs.push(idx);
    });
    return [...map.values()];
  }, [items]);

  const handleGoToContext = () => {
    if (items.length === 0) return;
    setActiveLeagueIdx(0); setStep('context'); window.scrollTo(0, 0);
  };

  const handleAdvanceFromContext = () => {
    if (!user) { setStep('auth'); window.scrollTo(0, 0); return; }
    if (!user.sleeperUsername) { setStep('sleeper'); window.scrollTo(0, 0); return; }
    handleProceedToPayment();
  };

  const handleProceedToPayment = async () => {
    // This runs from the payment_loading spinner state (including the
    // magic-link return path), so every early exit MUST route to a step the
    // user can act on — a bare return strands them on the spinner forever.
    if (items.length === 0) {
      setStep(product === 'roster' ? 'league' : 'build');
      window.scrollTo(0, 0); return;
    }
    const missingTimeline = leagueGroups.some((g) => !leagueContext[g.key]?.timeline);
    if (missingTimeline) {
      setContextError('Pick where each team is in its competitive cycle so the review fits your situation.');
      setStep('context');
      window.scrollTo(0, 0); return;
    }
    setContextError(null); setSubmitting(true); setPaymentError(null);

    const apiItems = items.map((item, idx) => {
      const lc = leagueContext[itemLeagueKey(item)] || {};
      const ic = itemContext[idx] || {};
      const context = {
        ...(ic.proposer ? { proposer: ic.proposer } : {}),
        ...(ic.theAsk ? { theAsk: ic.theAsk } : {}),
        ...(ic.theAsk === 'Something else' && ic.theAskOther?.trim() ? { theAskOther: ic.theAskOther.trim() } : {}),
        ...(ic.worry?.trim() ? { worry: ic.worry.trim() } : {}),
        league: {
          ...(lc.timeline ? { timeline: lc.timeline } : {}),
          ...(lc.philosophy?.trim() ? { philosophy: lc.philosophy.trim() } : {}),
          ...(lc.dynamics?.trim() ? { dynamics: lc.dynamics.trim() } : {}),
        },
      };
      if (item.type === 'roster') {
        // The focus question rides in `question`; league context stays in context.league.
        return {
          type: 'roster',
          leagueId: item.leagueId,
          ...(ic.worry?.trim() ? { question: ic.worry.trim() } : {}),
          context: { league: context.league },
        };
      }
      if (item.type === 'trade') return { type: 'trade', tradeId: item.tradeId, context };
      if (item.type === 'hypothetical') return { type: 'hypothetical', leagueId: item.leagueId, payload: item.payload, context };
      if (item.type === 'question') return { type: 'question', question: item.question, context };
      return { ...item, context };
    });

    try {
      const r = await analystAPI.initiateReview({
        items: apiItems,
        analystId: analyst.id,
        ...(product === 'roster' ? { productType: 'roster_review' } : {}),
      });
      setClientSecret(r.data.clientSecret); setReviewId(r.data.reviewId);
      // Clear persisted wizard state now that we've submitted
      try { ['items', 'lctx', 'ictx'].forEach((k) => sessionStorage.removeItem(`ft_ar_${k}_${slug}`)); } catch {}
      setStep('payment');
    } catch (err) {
      const status = err?.response?.status;
      // Beacon the failure so it shows up server-side — checkout failures
      // otherwise leave no trace when the request never reaches the backend
      // (extension interference, edge blocks, offline).
      try {
        trackEvent('analyst_checkout_error', {
          metadata: {
            status: status || null,
            message: err?.response?.data?.error || err?.message || 'unknown',
            itemCount: apiItems.length,
            analystSlug: slug,
          },
        });
      } catch {}
      // 401 = the saved session went stale server-side while the client still
      // thinks it's logged in — retrying would 401 forever. Re-run the
      // magic-link step; the auth effect auto-resumes payment after login.
      if (status === 401) {
        setSubmitting(false);
        setStep('auth');
        window.scrollTo(0, 0);
        return;
      }
      setPaymentError(
        err?.response?.data?.error ||
        (status
          ? `Something went wrong (HTTP ${status}). Please try again.`
          : "Couldn't reach the server — the request never made it out of your browser. Check your connection, and if it keeps happening, try disabling browser extensions or another browser.")
      );
    }
    setSubmitting(false);
  };

  if (loading || !analyst) {
    return (
      <div className="ft-view" style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
        <span style={{ color: 'var(--ft-ink-3)', fontSize: 15 }}>Loading…</span>
      </div>
    );
  }

  const isWizard = !['profile', 'success'].includes(step);
  const offersRoster = !!analyst.rosterReviewPriceInCents;

  // ── Right rail: timeline on profile, order summary in wizard ──
  const RailTimeline = () => (
    <div style={{ padding: '24px 20px' }}>
      <span className="ft-k" style={{ display: 'block', marginBottom: 20 }}>How it works</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {[
          offersRoster
            ? { n: 1, title: 'You send', items: ['A trade to review — or', 'One league for a full roster read', 'A focus question, optional'], note: 'Trade reviews take up to 3 items.' }
            : { n: 1, title: 'You send', items: ['A trade you\'re weighing', 'A trade you already made', 'A question about your roster'], note: 'Up to 3 items per review.' },
          { n: 2, title: `${analyst.name} reads`, items: ['Your full roster', 'League format + scoring', 'Standings + career arc', 'Recent trade history'], note: 'Pulled live from Sleeper.' },
          offersRoster
            ? { n: 3, title: 'You get back', items: ['Written take + letter grades', 'Trade — value · timeline · capital', 'Roster — positions · window · moves'], note: `Within ${analyst.slaHours}h, or full refund.` }
            : { n: 3, title: 'You get back', items: ['Per-item written take', 'Overall letter grade', 'Value · timeline · capital'], note: `Within ${analyst.slaHours}h, or full refund.` },
        ].map((row, i, arr) => (
          <div key={row.n} style={{ display: 'flex', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 28, flexShrink: 0 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--ft-dark)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{row.n}</div>
              {i < arr.length - 1 && <div style={{ width: 1, flex: 1, background: 'var(--ft-hairline)', margin: '6px 0' }} />}
            </div>
            <div style={{ paddingBottom: i < arr.length - 1 ? 24 : 0, paddingTop: 4, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ft-ink)', marginBottom: 8, letterSpacing: '-0.005em' }}>{row.title}</div>
              <ul style={{ listStyle: 'none', margin: '0 0 6px', padding: 0 }}>
                {row.items.map((it) => (
                  <li key={it} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginBottom: 5, fontSize: 12, color: 'var(--ft-ink-2)', lineHeight: 1.45 }}>
                    <div style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--ft-disabled)', flexShrink: 0, marginTop: 6 }} />
                    {it}
                  </li>
                ))}
              </ul>
              <span style={{ fontSize: 11, color: 'var(--ft-ink-3)' }}>{row.note}</span>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 28, padding: '14px 16px', background: 'var(--ft-green-soft)', border: '1px solid rgba(52,199,89,0.2)', borderRadius: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ft-green-ink)', marginBottom: 4 }}>{analyst.slaHours}h guarantee</div>
        <div style={{ fontSize: 12, color: 'var(--ft-ink-2)', lineHeight: 1.5 }}>If {analyst.name} misses the window, you get a full refund automatically.</div>
      </div>
      <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--ft-surface)', borderRadius: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ft-ink)' }}>{offersRoster ? 'Trade review' : 'Expert review'}</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--ft-ink)', letterSpacing: '-0.01em' }}>
            <Price analyst={analyst} />
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ft-ink-3)' }}>Up to 3 items · one flat fee</div>
      </div>
      {offersRoster && (
        <div style={{ marginTop: 10, padding: '14px 16px', background: 'rgba(255,122,0,0.07)', borderRadius: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ft-ink)' }}>Roster review</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--ft-ink)', letterSpacing: '-0.01em' }}>
              <Price analyst={analyst} product="roster" />
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ft-ink-3)' }}>One league · the full breakdown</div>
        </div>
      )}
    </div>
  );

  const WizardRail = () => (
    <div style={{ padding: '24px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--ft-hairline)' }}>
        {analyst.avatarUrl && !avatarBroken
          ? <img src={analyst.avatarUrl} alt="" onError={() => setAvatarBroken(true)} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          : <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--ft-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--ft-ink-2)', flexShrink: 0 }}>{analyst.name[0]}</div>
        }
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ft-ink)' }}>{analyst.name}</div>
          <div style={{ fontSize: 11, color: 'var(--ft-ink-3)' }}>
            {product === 'roster' ? <>Roster review · <Price analyst={analyst} product="roster" /></> : <>Expert review · <Price analyst={analyst} /></>}
          </div>
        </div>
      </div>
      {items.length > 0 ? (
        <>
          <span className="ft-k" style={{ display: 'block', marginBottom: 10 }}>
            {product === 'roster' ? 'Your league' : `Your items · ${items.length}/3`}
          </span>
          {items.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10, padding: '10px 12px', background: 'var(--ft-surface)', borderRadius: 10 }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--ft-dark)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>{idx + 1}</div>
              <div style={{ fontSize: 12, color: 'var(--ft-ink-2)', lineHeight: 1.45, minWidth: 0 }}>{itemLabel(item)}</div>
            </div>
          ))}
        </>
      ) : (
        <div style={{ padding: '12px 0' }}>
          <span className="ft-k" style={{ display: 'block', marginBottom: 8 }}>What you're building</span>
          {(product === 'roster' ? [
            'Pick one league',
            'Add a focus question, optional',
            'Get the full breakdown',
          ] : [
            'Add a trade you\'re weighing',
            'Or a trade you already made',
            'Or ask a question',
          ]).map((t) => (
            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8, fontSize: 12, color: 'var(--ft-ink-3)', lineHeight: 1.4 }}>
              <div style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--ft-disabled)', flexShrink: 0 }} />
              {t}
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 20, padding: '12px 14px', background: 'var(--ft-green-soft)', border: '1px solid rgba(52,199,89,0.2)', borderRadius: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ft-green-ink)', marginBottom: 3 }}>{analyst.slaHours}h or your money back</div>
        <div style={{ fontSize: 11, color: 'var(--ft-ink-2)' }}>Full refund if {analyst.name} misses the window.</div>
      </div>
    </div>
  );

  return (
    <div className="ft-view" style={{ padding: 0 }}>
      {/* Rail — timeline on profile, order summary in wizard */}
      {step === 'profile' && <ShellRail><RailTimeline /></ShellRail>}
      {isWizard && <ShellRail><WizardRail /></ShellRail>}

      {/* ── Full-bleed banner (profile step only) ── */}
      {step === 'profile' && (
        <div style={{ position: 'relative', width: '100%', height: 220, overflow: 'hidden' }}>
          {analyst.bannerUrl && !bannerBroken
            ? <img src={analyst.bannerUrl} alt="" onError={() => setBannerBroken(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ width: '100%', height: '100%', background: 'var(--ft-surface)' }} />
          }
          {/* Fade to white at bottom */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 45%, rgba(255,255,255,0.7) 80%, rgba(255,255,255,1) 100%)', pointerEvents: 'none' }} />
        </div>
      )}

      <div style={{ maxWidth: 680, margin: '0 auto', padding: step === 'profile' ? '0 20px 120px' : '24px 20px 120px' }}>

        {/* ── Analyst anchor — keeps wizard grounded in the profile context ── */}
        {isWizard && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid var(--ft-hairline)' }}>
            <button
              onClick={handleBack}
              aria-label="Back"
              style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                background: 'var(--ft-surface)', border: '1px solid var(--ft-hairline)',
                cursor: 'pointer', color: 'var(--ft-ink)', fontSize: 15,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ft-surface-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--ft-surface)'; }}
            >←</button>
            {analyst.avatarUrl && !avatarBroken
              ? <img src={analyst.avatarUrl} alt="" onError={() => setAvatarBroken(true)} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
              : <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--ft-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: 'var(--ft-ink-2)', flexShrink: 0 }}>{analyst.name[0]}</div>
            }
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ft-ink)', letterSpacing: '-0.005em' }}>{analyst.name}</div>
              <div style={{ fontSize: 12, color: 'var(--ft-ink-3)' }}>
                {product === 'roster' ? 'Roster review' : 'Expert review'} · <Price analyst={analyst} showBadge product={product} /> · {analyst.slaHours}h SLA
              </div>
            </div>
            <WizardProgress step={step} steps={product === 'roster' ? ROSTER_WIZARD_STEPS : WIZARD_STEPS} />
          </div>
        )}

        {/* ── Profile step ── */}
        {step === 'profile' && (
          <>
            {/* Avatar overlapping banner */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 22, marginTop: -60, position: 'relative', zIndex: 1, marginBottom: 32 }}>
              {analyst.avatarUrl && !avatarBroken ? (
                <img
                  src={analyst.avatarUrl} alt={analyst.name}
                  onError={() => setAvatarBroken(true)}
                  style={{ width: 130, height: 130, borderRadius: '50%', objectFit: 'cover', border: '4px solid var(--ft-bg)', boxShadow: '0 10px 40px rgba(0,0,0,0.15)', flexShrink: 0 }}
                />
              ) : (
                <div style={{ width: 130, height: 130, borderRadius: '50%', background: 'var(--ft-surface)', border: '4px solid var(--ft-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 44, fontWeight: 700, color: 'var(--ft-ink-2)', flexShrink: 0 }}>
                  {analyst.name[0]}
                </div>
              )}
              <div style={{ paddingBottom: 8, minWidth: 0 }}>
                <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.02, color: 'var(--ft-ink)', margin: '0 0 10px' }}>
                  {analyst.name}
                </h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {analyst.twitterHandle && (
                    <a href={`https://x.com/${analyst.twitterHandle}`} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--ft-surface)', border: '1px solid var(--ft-hairline)', borderRadius: 'var(--ft-r-pill)', padding: '5px 12px', textDecoration: 'none', fontSize: 12, fontWeight: 600, color: 'var(--ft-ink-2)', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--ft-surface-hover)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--ft-surface)'}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                      @{analyst.twitterHandle}
                    </a>
                  )}
                  {analyst.podcastUrl && (
                    <a href={analyst.podcastUrl} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--ft-surface)', border: '1px solid var(--ft-hairline)', borderRadius: 'var(--ft-r-pill)', padding: '5px 12px', textDecoration: 'none', fontSize: 12, fontWeight: 600, color: 'var(--ft-ink-2)', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--ft-surface-hover)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--ft-surface)'}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
                      Podcast
                    </a>
                  )}
                </div>
              </div>
            </div>

            {!offersRoster ? (
              <>
                <span className="ft-k" style={{ marginBottom: 14, display: 'block' }}>Paid trade review · <Price analyst={analyst} /></span>
                <h1 className="ft-hero" style={{ marginBottom: 18 }}>Get a real take on your trade.</h1>
                <p className="ft-sub" style={{ marginBottom: 28 }}>
                  Send up to 3 items — a trade you're considering, a trade you made, or a general question. Real analysis from someone who reads your roster, your league, and your situation, then tells you if it's right for <em style={{ color: 'var(--ft-ink)', fontStyle: 'italic' }}>you</em>.
                </p>

                {/* Primary CTA */}
                <button
                  onClick={() => handleStart('trade')}
                  className="ft-btn"
                  style={{ width: '100%', padding: '17px 40px', fontSize: 16 }}
                >
                  Start your review →
                </button>
                <p style={{ textAlign: 'center', marginTop: 14, fontSize: 14, color: 'var(--ft-ink-3)', fontWeight: 500 }}>
                  {analyst.slaHours}h or your money back · <Price analyst={analyst} showBadge /> flat
                </p>
              </>
            ) : (
              <>
                <span className="ft-k" style={{ marginBottom: 14, display: 'block' }}>
                  Analyst reviews · from {fmtPrice(Math.min(analyst.priceInCents, analyst.rosterReviewPriceInCents))}
                </span>
                <h1 className="ft-hero" style={{ marginBottom: 14 }}>Pick your review.</h1>
                <p className="ft-sub" style={{ marginBottom: 20 }}>
                  Real analysis from someone who reads your roster, your league, and your situation. Two ways in.
                </p>

                {/* Product menu */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
                  {[
                    {
                      key: 'trade',
                      kicker: 'Trade review',
                      kickerColor: 'rgba(140,100,255,0.85)',
                      isNew: false,
                      price: analyst.priceInCents,
                      compareAt: analyst.compareAtPriceInCents,
                      name: 'A real take on your trade.',
                      desc: "Up to 3 items — a trade you're weighing, one you already made, or an open question.",
                      bullets: ['Per-item written take', 'Overall letter grade', 'Value, timeline, and capital reads'],
                      dot: 'rgba(180,140,255,0.75)',
                      bg: 'rgba(180,140,255,0.06)',
                      border: '1px solid rgba(180,140,255,0.2)',
                      cta: 'Start a trade review',
                      ctaClass: 'ft-btn ghost',
                    },
                    {
                      key: 'roster',
                      kicker: 'Roster review',
                      kickerColor: 'var(--ft-brand-ink)',
                      isNew: true,
                      price: analyst.rosterReviewPriceInCents,
                      compareAt: analyst.rosterReviewCompareAtPriceInCents,
                      name: 'Your whole roster, graded.',
                      desc: 'Pick one league. Get a full read on where your team actually stands — and what to do next.',
                      bullets: ['Grades by position — QB, RB, WR, TE, picks', 'Contention window verdict', 'Written breakdown and recommended moves'],
                      dot: 'var(--ft-brand)',
                      bg: 'rgba(255,122,0,0.05)',
                      border: '1px solid rgba(255,122,0,0.28)',
                      cta: 'Start a roster review',
                      ctaClass: 'ft-btn',
                    },
                  ].map((p) => {
                    const onSale = p.compareAt && p.compareAt > p.price;
                    return (
                      <div
                        key={p.key}
                        onClick={() => handleStart(p.key)}
                        style={{ display: 'flex', flexDirection: 'column', background: p.bg, border: p.border, borderRadius: 16, padding: '18px 18px 16px', cursor: 'pointer', transition: 'transform 0.18s var(--ft-ease), box-shadow 0.18s var(--ft-ease)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                          <span className="ft-k" style={{ color: p.kickerColor }}>{p.kicker}</span>
                          {p.isNew && (
                            <span style={{ background: 'var(--ft-brand-soft)', color: 'var(--ft-brand-ink)', borderRadius: 'var(--ft-r-pill)', padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>New</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8, whiteSpace: 'nowrap' }}>
                          <span style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ft-ink)', fontFamily: "'SF Mono', ui-monospace, Menlo, monospace" }}>
                            {fmtPrice(p.price)}
                          </span>
                          {onSale && (
                            <>
                              <s style={{ fontSize: 14, fontWeight: 500, color: 'var(--ft-ink-3)', fontFamily: "'SF Mono', ui-monospace, Menlo, monospace", textDecorationThickness: 1 }}>
                                {fmtPrice(p.compareAt)}
                              </s>
                              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ft-green-ink)', letterSpacing: '0.01em' }}>Limited time</span>
                            </>
                          )}
                        </div>
                        <div style={{ fontSize: 16.5, fontWeight: 700, letterSpacing: '-0.015em', marginBottom: 4, color: 'var(--ft-ink)' }}>{p.name}</div>
                        <p style={{ fontSize: 13, color: 'var(--ft-ink-2)', lineHeight: 1.5, marginBottom: 12 }}>{p.desc}</p>
                        <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0, display: 'grid', gap: 6 }}>
                          {p.bullets.map((t) => (
                            <li key={t} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, lineHeight: 1.45, color: 'var(--ft-ink)' }}>
                              <span style={{ width: 4, height: 4, borderRadius: '50%', background: p.dot, flexShrink: 0, marginTop: 6 }} />
                              {t}
                            </li>
                          ))}
                        </ul>
                        <button onClick={() => handleStart(p.key)} className={p.ctaClass} style={{ width: '100%', marginTop: 'auto' }}>
                          {p.cta}
                        </button>
                      </div>
                    );
                  })}
                </div>
                <p style={{ textAlign: 'center', marginTop: 14, fontSize: 12.5, fontWeight: 600, color: 'var(--ft-green-ink)' }}>
                  {analyst.slaHours}h or your money back — full refund, automatic
                </p>
              </>
            )}

            {/* Detail sections — on two-product pages a pill toggle switches
                between the trade and roster pitches; single-product pages just
                show the trade sections. */}
            {offersRoster && (
              <div style={{ display: 'flex', gap: 8, marginTop: 48 }}>
                {[['trade', 'Trade review'], ['roster', 'Roster review']].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setDetailProduct(k)}
                    className={`ft-fpill${detailProduct === k ? ' on' : ''}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {(!offersRoster || detailProduct === 'trade') && (<>
            <div style={{ position: 'relative', marginTop: offersRoster ? 20 : 48, marginBottom: 48, background: 'rgba(180,140,255,0.06)', border: '1px solid rgba(180,140,255,0.2)', borderRadius: 20, padding: '28px 28px 24px' }}>
              {!isMobile && (
                <div style={{ position: 'absolute', top: 18, right: 18, background: 'var(--ft-dark)', color: '#fff', borderRadius: 'var(--ft-r-pill)', padding: '5px 14px', fontSize: 13, fontWeight: 800 }}>
                  <Price analyst={analyst} showBadge /> flat
                </div>
              )}
              <span className="ft-k" style={{ color: 'rgba(140,100,255,0.85)', display: 'block', marginBottom: 6 }}>{offersRoster ? 'Trade review · how it works' : 'How it works'}</span>
              <h3 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--ft-ink)', margin: '0 0 8px', lineHeight: 1.08, maxWidth: 500 }}>
                Deeper than a value chart. In {analyst.slaHours}h, or your money back.
              </h3>
              <p style={{ fontSize: 15, color: 'var(--ft-ink-2)', margin: '0 0 24px', lineHeight: 1.5, maxWidth: 540 }}>
                {analyst.name} reads your real situation — then sends a structured take, not just a number.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12 }}>
                {[
                  { label: 'You send', items: ['A trade you\'re considering', 'A trade you made', 'A general question'], note: 'Up to 3 items.' },
                  { label: `${analyst.name} reads`, items: ['Your roster', 'League format + scoring', 'Standings + career arc', 'Recent trade history'], note: 'Pulled live from Sleeper.' },
                  { label: 'You get back', items: ['Per-item written take', 'Overall letter grade', 'Trade value · timeline · capital'], note: `Within ${analyst.slaHours}h, or full refund.` },
                ].map((col, colIdx) => (
                  <div key={col.label} style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(180,140,255,0.12)', borderRadius: 14, padding: '18px 18px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(180,140,255,0.18)', color: 'rgba(100,60,200,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>{colIdx + 1}</div>
                      <span className="ft-k">{col.label}</span>
                    </div>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {col.items.map((it) => (
                        <li key={it} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6, fontSize: 13, color: 'var(--ft-ink)', lineHeight: 1.45 }}>
                          <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(180,140,255,0.75)', flexShrink: 0, marginTop: 7 }} />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                    <p style={{ fontSize: 11, color: 'var(--ft-ink-3)', marginTop: 10, marginBottom: 0 }}>{col.note}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Sample verdict */}
            <div style={{ marginTop: 72 }}>
              <span className="ft-k" style={{ display: 'block', marginBottom: 14 }}>{offersRoster ? 'Trade review · what you get back' : 'What you get back'}</span>
              <h3 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.1, color: 'var(--ft-ink)', margin: '0 0 8px' }}>
                An overall grade, three reads, and a written take.
              </h3>
              <p style={{ fontSize: 15, color: 'var(--ft-ink-3)', marginBottom: 24 }}>Shape's the same every time. Specifics come from your real data.</p>
              <div style={{ background: 'var(--ft-green-soft)', border: '1px solid rgba(52,199,89,0.25)', borderRadius: 18, padding: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                  <div style={{ width: 70, height: 70, borderRadius: '50%', background: 'var(--ft-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 30, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>B+</span>
                  </div>
                  <div>
                    <span className="ft-k" style={{ color: 'var(--ft-green-ink)', display: 'block', marginBottom: 10 }}>Illustrative · verdict shape</span>
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                      {[['Trade value', 'You win it'], ['Timeline fit', 'Good fit'], ['Capital impact', 'Neutral']].map(([k, v]) => (
                        <div key={k}>
                          <span className="ft-k" style={{ display: 'block', marginBottom: 2 }}>{k}</span>
                          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ft-ink)' }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 22, padding: '16px 18px', background: 'rgba(52,199,89,0.06)', border: '1px solid rgba(52,199,89,0.12)', borderRadius: 12 }}>
                  <p style={{ fontSize: 14.5, lineHeight: 1.6, color: 'var(--ft-ink)', fontStyle: 'italic', margin: 0 }}>
                    <span style={{ fontFamily: 'Georgia, serif', color: 'rgba(180,140,255,0.5)', fontSize: 22, verticalAlign: '-6px', marginRight: 4 }}>"</span>
                    The written take lands here — referencing your actual roster, your actual record, the actual league you're in. Should you do it, should you counter, what to ask for instead. A few sentences, the kind a friend who studies your team would write.
                  </p>
                </div>
              </div>
            </div>
            </>)}

            {/* Roster review — how it works + verdict shape (two-product pages) */}
            {offersRoster && detailProduct === 'roster' && (<>
            <div style={{ position: 'relative', marginTop: 20, background: 'rgba(180,140,255,0.06)', border: '1px solid rgba(180,140,255,0.2)', borderRadius: 20, padding: '26px 26px 22px' }}>
              {!isMobile && (
                <div style={{ position: 'absolute', top: 18, right: 18, background: 'var(--ft-dark)', color: '#fff', borderRadius: 'var(--ft-r-pill)', padding: '5px 14px', fontSize: 13, fontWeight: 800 }}>
                  <Price analyst={analyst} showBadge product="roster" />
                </div>
              )}
              <span className="ft-k" style={{ color: 'rgba(140,100,255,0.85)', display: 'block', marginBottom: 6 }}>Roster review · how it works</span>
              <h3 style={{ fontSize: isMobile ? 21 : 24, fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--ft-ink)', margin: '0 0 8px', lineHeight: 1.1, maxWidth: 460 }}>
                Your whole team, read by a human. In {analyst.slaHours}h, or your money back.
              </h3>
              <p style={{ fontSize: 14.5, color: 'var(--ft-ink-2)', margin: '0 0 22px', lineHeight: 1.5, maxWidth: 500 }}>
                {analyst.name} reads your real situation — then sends a full plan, not just a number.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12 }}>
                {[
                  { n: 1, t: 'You pick a league', s: 'One league per review. Add a focus question if there\'s something specific on your mind.' },
                  { n: 2, t: `${analyst.name} reads`, s: 'Roster, format and scoring, standings, draft capital, trade history — pulled live from Sleeper.' },
                  { n: 3, t: 'You get the verdict', s: `Position grades, a contention window call, and a written plan. Within ${analyst.slaHours}h, or full refund.` },
                ].map((col) => (
                  <div key={col.n} style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(180,140,255,0.12)', borderRadius: 14, padding: '18px 16px 14px' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(180,140,255,0.18)', color: 'rgba(100,60,200,0.9)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, marginBottom: 10 }}>{col.n}</div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: 6, color: 'var(--ft-ink)' }}>{col.t}</div>
                    <p style={{ fontSize: 12.5, color: 'var(--ft-ink-2)', lineHeight: 1.5, margin: 0 }}>{col.s}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Roster verdict shape */}
            <div style={{ marginTop: 72 }}>
              <span className="ft-k" style={{ display: 'block', marginBottom: 14 }}>Roster review · what you get back</span>
              <h3 style={{ fontSize: isMobile ? 23 : 28, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.1, color: 'var(--ft-ink)', margin: '0 0 8px' }}>
                Six grades, a window, and a plan.
              </h3>
              <p style={{ fontSize: 15, color: 'var(--ft-ink-3)', marginBottom: 24 }}>Shape's the same every time. Specifics come from your real data.</p>
              <div style={{ background: 'var(--ft-green-soft)', border: '1px solid rgba(52,199,89,0.25)', borderRadius: 18, padding: 26 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', marginBottom: 20 }}>
                  <div style={{ width: 70, height: 70, borderRadius: '50%', background: 'var(--ft-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 30, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>B</span>
                  </div>
                  <div>
                    <span className="ft-k" style={{ color: 'var(--ft-green-ink)', display: 'block', marginBottom: 8 }}>Illustrative · verdict shape</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ft-ink)' }}>Contention window</span>
                      <span style={{ background: 'var(--ft-dark)', color: '#fff', borderRadius: 'var(--ft-r-pill)', padding: '4px 11px', fontSize: 11.5, fontWeight: 700 }}>Retool</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)', gap: 8, marginBottom: 20 }}>
                  {[['QB', 'A-'], ['RB', 'C+'], ['WR', 'B+'], ['TE', 'B'], ['Picks', 'A']].map(([pos, g]) => (
                    <div key={pos} style={{ background: 'rgba(255,255,255,0.75)', border: '1px solid rgba(52,199,89,0.18)', borderRadius: 12, padding: '12px 8px 10px', textAlign: 'center' }}>
                      <span className="ft-k" style={{ display: 'block', marginBottom: 5, fontSize: 10 }}>{pos}</span>
                      <b style={{ fontFamily: "'SF Mono', ui-monospace, Menlo, monospace", fontSize: 19, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ft-ink)' }}>{g}</b>
                    </div>
                  ))}
                </div>
                <div style={{ background: 'rgba(52,199,89,0.06)', border: '1px solid rgba(52,199,89,0.12)', borderRadius: 12, padding: '16px 18px' }}>
                  <p style={{ fontSize: 14.5, lineHeight: 1.6, color: 'var(--ft-ink)', fontStyle: 'italic', margin: 0 }}>
                    <span style={{ fontFamily: 'Georgia, serif', color: 'rgba(180,140,255,0.5)', fontSize: 22, verticalAlign: '-6px', marginRight: 4 }}>"</span>
                    The written breakdown lands here — your actual roster, your actual standings, the league you're really in. Where you're strong, where you're thin, and whether the window you think you have is the one you actually have.
                  </p>
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(52,199,89,0.15)' }}>
                    <span className="ft-k" style={{ display: 'block', marginBottom: 8 }}>Recommended moves</span>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
                      {['The one position to spend on before the deadline', 'Who to shop while their value holds', 'What your picks are actually for'].map((t) => (
                        <li key={t} style={{ display: 'flex', gap: 9, fontSize: 13.5, lineHeight: 1.5, color: 'var(--ft-ink)' }}>
                          <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--ft-green)', flexShrink: 0, marginTop: 7 }} />
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            </>)}

            {/* FAQ */}
            <div style={{ marginTop: 72 }}>
              <span className="ft-k" style={{ display: 'block', marginBottom: 14 }}>Frequently asked</span>
              <h3 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.1, color: 'var(--ft-ink)', margin: '0 0 24px' }}>
                Sorting the obvious questions.
              </h3>
              <div style={{ display: 'grid', gap: 8 }}>
                {[
                  { q: `What if ${analyst.name} doesn't respond within ${analyst.slaHours} hours?`, a: 'Full refund. No questions, no DMs, no hassle — the system flags it automatically.' },
                  { q: 'What\'s "up to 3 items"?', a: `You can mix and match: a trade you got offered, a hypothetical you're floating, an open question. Up to 3 in a single review for the flat ${fmtPrice(analyst.priceInCents)}${analyst.compareAtPriceInCents && analyst.compareAtPriceInCents > analyst.priceInCents ? ` (regularly ${fmtPrice(analyst.compareAtPriceInCents)})` : ''}.` },
                  ...(offersRoster ? [
                    { q: 'What\'s in a roster review?', a: `The full read on one league: a grade for every position group plus your draft capital, a contention window call — contend, retool, or rebuild — and a written plan with the moves ${analyst.name} would actually make. ${fmtPrice(analyst.rosterReviewPriceInCents)}${analyst.rosterReviewCompareAtPriceInCents && analyst.rosterReviewCompareAtPriceInCents > analyst.rosterReviewPriceInCents ? ` (regularly ${fmtPrice(analyst.rosterReviewCompareAtPriceInCents)})` : ''} flat.` },
                  ] : []),
                  { q: `Does ${analyst.name} need access to my league?`, a: 'No. Link your Sleeper username and we pull your roster, league format, and history from the public Sleeper API. Read-only.' },
                  { q: 'How is this different from a value chart?', a: 'A value chart says if a trade is fair. The analyst tells you if it\'s right for you — given your competitive cycle, your roster construction, and where the league is at.' },
                ].map((faq, i) => (
                  <details key={i} style={{ background: 'var(--ft-surface)', border: '1px solid var(--ft-hairline)', borderRadius: 14, padding: '18px 22px' }}>
                    <summary style={{ cursor: 'pointer', listStyle: 'none', fontSize: 15, fontWeight: 600, letterSpacing: '-0.005em', color: 'var(--ft-ink)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {faq.q}
                      <span style={{ color: 'var(--ft-ink-3)', fontSize: 20, fontWeight: 300 }}>+</span>
                    </summary>
                    <p style={{ marginTop: 12, fontSize: 13.5, lineHeight: 1.6, color: 'var(--ft-ink-2)', marginBottom: 0 }}>{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>

            {/* Bottom CTA */}
            <div style={{ marginTop: 64, paddingBottom: 40 }}>
              {offersRoster ? (
                <>
                  <button onClick={() => handleStart('roster')} className="ft-btn" style={{ width: '100%', padding: '17px 40px', fontSize: 16 }}>
                    Start a roster review →
                  </button>
                  <p style={{ textAlign: 'center', marginTop: 12, fontSize: 12, color: 'var(--ft-ink-3)' }}>
                    <Price analyst={analyst} showBadge product="roster" /> flat · {analyst.slaHours}h · full refund if late
                  </p>
                  <button onClick={() => handleStart('trade')} className="ft-btn ghost" style={{ width: '100%', padding: '15px 40px', fontSize: 15, marginTop: 14 }}>
                    Start a trade review
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => handleStart('trade')} className="ft-btn" style={{ width: '100%', padding: '17px 40px', fontSize: 16 }}>
                    Start your review →
                  </button>
                  <p style={{ textAlign: 'center', marginTop: 12, fontSize: 12, color: 'var(--ft-ink-3)' }}>
                    <Price analyst={analyst} showBadge /> flat · {analyst.slaHours}h · full refund if late
                  </p>
                </>
              )}
            </div>
          </>
        )}

        {/* ── Build ── */}
        {/* ── Pick a league (roster review step 1) ── */}
        {step === 'league' && (
          <div>
            <div style={{ marginBottom: 28 }}>
              <span className="ft-k" style={{ display: 'block', marginBottom: 12 }}>Step 1 of 3</span>
              <h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--ft-ink)', letterSpacing: '-0.03em', lineHeight: 1.05, margin: '0 0 10px' }}>
                Pick your league.
              </h2>
              <p style={{ fontSize: 15, color: 'var(--ft-ink-2)', lineHeight: 1.55, margin: 0 }}>
                One league per review. {analyst.name} reads the whole roster — players, picks, standings, history.
              </p>
            </div>

            {paymentError && <p style={{ color: 'var(--ft-red)', fontSize: 13, marginBottom: 20 }}>{paymentError}</p>}

            <PickLeagueTab
              user={user}
              sharedSleeper={sharedSleeper}
              onSharedSleeper={setSharedSleeper}
              selectedLeagueId={items[0]?.type === 'roster' ? items[0].leagueId : null}
              onPick={(l) => {
                setItems([{
                  type: 'roster',
                  leagueId: l.sleeperLeagueId,
                  _leagueName: l.name,
                  _leagueAvatar: l.avatar || null,
                  _leagueMeta: { season: l.season, numTeams: l.numTeams, isSuperFlex: l.isSuperFlex },
                }]);
              }}
            />

            {items[0]?.type === 'roster' && (
              <button
                onClick={() => { setActiveLeagueIdx(0); setStep('context'); window.scrollTo(0, 0); }}
                className="ft-btn"
                style={{ width: '100%', padding: '17px 40px', fontSize: 16, marginTop: 22 }}
              >
                {`Continue with ${items[0]._leagueName || 'this league'} →`}
              </button>
            )}
          </div>
        )}

        {step === 'build' && (
          <div>
            <div className={`ft-toast${addedToast ? ' show' : ''}`}>
              {justAddedIndex !== null ? `Trade ${justAddedIndex + 1} added.` : ''}
            </div>

            <div style={{ marginBottom: 32 }}>
              <span className="ft-k" style={{ display: 'block', marginBottom: 12 }}>Step 1 of 3</span>
              <h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--ft-ink)', letterSpacing: '-0.03em', lineHeight: 1.05, margin: '0 0 10px' }}>
                {buildView === 'hub' ? 'Select your trades.' : 'Build the trade.'}
              </h2>
              <p style={{ fontSize: 15, color: 'var(--ft-ink-2)', lineHeight: 1.55, margin: 0 }}>
                Add up to 3 — a trade you're weighing, a deal you already made, or a straight-up question.
              </p>
            </div>

            {paymentError && <p style={{ color: 'var(--ft-red)', fontSize: 13, marginBottom: 20 }}>{paymentError}</p>}

            {buildView === 'hub' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12, marginBottom: 18 }}>
                  {[0, 1, 2].map((i) => {
                    const item = items[i];

                    if (!item) {
                      const isNext = i === items.length;
                      return (
                        <button
                          key={i}
                          disabled={!isNext}
                          onClick={() => { setActiveTab('build'); setBuildView('form'); }}
                          style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                            minHeight: 128, background: 'transparent',
                            border: `1.5px dashed ${isNext ? 'var(--ft-disabled)' : 'var(--ft-hairline)'}`,
                            borderRadius: 14, padding: '20px 12px',
                            color: isNext ? 'var(--ft-ink-3)' : 'var(--ft-disabled)',
                            cursor: isNext ? 'pointer' : 'default',
                            transition: 'all 0.18s var(--ft-ease)',
                          }}
                          onMouseEnter={(e) => { if (isNext) { e.currentTarget.style.borderColor = 'var(--ft-ink-3)'; e.currentTarget.style.color = 'var(--ft-ink-2)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                          onMouseLeave={(e) => { if (isNext) { e.currentTarget.style.borderColor = 'var(--ft-disabled)'; e.currentTarget.style.color = 'var(--ft-ink-3)'; e.currentTarget.style.transform = 'none'; } }}
                        >
                          <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
                          <span style={{ fontSize: 13.5, fontWeight: 700 }}>Trade {i + 1}</span>
                          <span style={{ fontSize: 11 }}>{isNext ? 'Tap to add' : `Add trade ${items.length} first`}</span>
                        </button>
                      );
                    }

                    const isQuestion = item.type === 'question';
                    const isExecuted = !!item._sleeperTradeId;
                    const badgeLabel = isExecuted ? 'Trade' : itemTypeBadge(item.type);
                    const leagueName = item._leagueName || item._tradeData?.leagueName;
                    const counterparty = item._counterparty || item._tradeData?.vs;
                    const giving = item.payload?.giving || [];
                    const getting = item.payload?.getting || [];
                    const badgeBg = isExecuted ? 'var(--ft-green-soft)' : 'rgba(180,140,255,0.1)';
                    const badgeColor = isExecuted ? 'var(--ft-green-ink)' : '#6A3D9A';
                    return (
                      <div
                        key={i}
                        style={{
                          background: 'var(--ft-surface)', borderRadius: 14, padding: '13px 14px',
                          boxShadow: justAddedIndex === i ? '0 0 0 3px var(--ft-green-soft)' : 'none',
                          transition: 'box-shadow 0.4s ease',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 9 }}>
                          <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', color: 'var(--ft-ink-2)', fontSize: 9.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                          <span style={{ background: badgeBg, color: badgeColor, borderRadius: 4, padding: '2.5px 6px', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{badgeLabel}</span>
                          <div style={{ flex: 1 }} />
                          <button
                            onClick={() => removeItem(i)}
                            style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', border: 'none', color: 'var(--ft-ink-3)', fontSize: 12, lineHeight: 1, cursor: 'pointer', flexShrink: 0 }}
                          >×</button>
                        </div>
                        {isQuestion ? (
                          <p style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--ft-ink)', margin: 0, lineHeight: 1.4 }}>"{item.question}"</p>
                        ) : (
                          <>
                            {leagueName && (
                              <div style={{ fontSize: 10.5, color: 'var(--ft-ink-3)', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {leagueName}{counterparty ? ` · vs ${counterparty}` : ''}
                              </div>
                            )}
                            <div style={{ fontSize: 11.5, lineHeight: 1.5 }}>
                              <span style={{ color: 'var(--ft-ink-3)' }}>Give </span>
                              <span style={{ color: 'var(--ft-ink)', fontWeight: 500 }}>{giving.length ? giving.map((g) => g.name).join(', ') : '—'}</span>
                            </div>
                            <div style={{ fontSize: 11.5, lineHeight: 1.5 }}>
                              <span style={{ color: 'var(--ft-ink-3)' }}>Get </span>
                              <span style={{ color: 'var(--ft-ink)', fontWeight: 500 }}>{getting.length ? getting.map((g) => g.name).join(', ') : '—'}</span>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                <p style={{ fontSize: 13, color: 'var(--ft-ink-2)', marginBottom: 22 }}>
                  {items.length === 0
                    ? 'Add at least one to continue.'
                    : items.length < 3
                    ? `${items.length} of 3 added — add another, or continue with what you have.`
                    : 'All 3 added. Remove one to swap it out.'}
                </p>

                <button
                  onClick={handleGoToContext}
                  className="ft-btn"
                  disabled={items.length === 0}
                  style={{ width: '100%', padding: '17px 40px', fontSize: 16 }}
                >
                  {`Continue${items.length ? ` with ${items.length} ${items.length === 1 ? 'trade' : 'trades'}` : ''} →`}
                </button>
              </>
            )}

            {buildView === 'form' && (
              <>
                <button
                  onClick={() => setBuildView('hub')}
                  className="ft-btn ghost sm"
                  style={{ marginBottom: 20 }}
                >
                  ← Back to your trades
                </button>

                {/* Option picker — always shown when no sub-tab is active */}
                {activeTab === 'build' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      { key: 'build_live', icon: '↔', label: 'Build a trade', desc: 'Connect your Sleeper league and construct a trade.' },
                      { key: 'trades', icon: '↺', label: 'Review a past trade', desc: 'A deal you already made — grade it after the fact.' },
                      { key: 'question', icon: '?', label: 'Ask a question', desc: 'Roster decisions, strategy, anything on your mind.' },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => setActiveTab(opt.key)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 16,
                          textAlign: 'left', padding: '18px 20px',
                          background: '#fff', border: '1px solid var(--ft-hairline)',
                          borderRadius: 16, cursor: 'pointer',
                          transition: 'all 0.18s var(--ft-ease)', width: '100%',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1D1D1F'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ft-hairline)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'none'; }}
                      >
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--ft-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: 'var(--ft-ink)', flexShrink: 0, fontWeight: 600 }}>{opt.icon}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ft-ink)', letterSpacing: '-0.01em', marginBottom: 3 }}>{opt.label}</div>
                          <div style={{ fontSize: 13, color: 'var(--ft-ink-3)', lineHeight: 1.4 }}>{opt.desc}</div>
                        </div>
                        <span style={{ color: 'var(--ft-ink-3)', fontSize: 18, flexShrink: 0, fontWeight: 300 }}>›</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Sub-tab content — back chip above, content below */}
                {activeTab !== 'build' && (
                  <>
                    <button
                      onClick={() => setActiveTab('build')}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: 'var(--ft-bg)', border: '1px solid var(--ft-hairline)',
                        borderRadius: 'var(--ft-r-pill)', padding: '6px 12px',
                        fontSize: 12, fontWeight: 500, color: 'var(--ft-ink-2)',
                        cursor: 'pointer', marginBottom: 16,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      ← All options
                    </button>
                    {activeTab === 'build_live' && <BuildTradeTab user={user} onAddItem={handleAddItem} maxReached={maxReached} sharedSleeper={sharedSleeper} onSharedSleeper={setSharedSleeper} onLinkSleeper={async (username) => { await analystAPI.linkSleeper(username); await refreshUser(); }} />}
                    {activeTab === 'trades' && <MyTradesTab user={user} items={items} onAddItem={handleAddItem} maxReached={maxReached} sharedSleeper={sharedSleeper} onSharedSleeper={setSharedSleeper} />}
                    {activeTab === 'question' && <AskQuestionTab onAddItem={handleAddItem} maxReached={maxReached} />}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Context ── */}
        {step === 'context' && (() => {
          const setLeagueCtx = (key, field, val) => setLeagueContext((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: val } }));
          const setItemCtx = (idx, field, val) => setItemContext((p) => ({ ...p, [idx]: { ...(p[idx] || {}), [field]: val } }));

          const ContextChip = ({ selected, onClick, children }) => (
            <button
              onClick={onClick}
              style={{
                padding: '10px 18px', background: selected ? 'var(--ft-dark)' : 'var(--ft-surface)',
                border: `1px solid ${selected ? 'var(--ft-dark)' : 'var(--ft-hairline)'}`,
                borderRadius: 'var(--ft-r-pill)', fontSize: 13,
                fontWeight: selected ? 600 : 400, color: selected ? '#fff' : 'var(--ft-ink-2)',
                cursor: 'pointer', transition: 'all 0.15s ease', fontFamily: ff,
              }}
            >{children}</button>
          );

          const fieldLabelStyle = { fontSize: 14, fontWeight: 600, color: 'var(--ft-ink)', marginBottom: 10, display: 'block' };

          return (
            <div>
              <span className="ft-k" style={{ display: 'block', marginBottom: 14 }}>Step 2 of 3</span>
              <h2 style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--ft-ink)', margin: '0 0 10px', lineHeight: 1.05 }}>
                A few quick things.
              </h2>
              <p style={{ fontSize: 15, color: 'var(--ft-ink-2)', marginBottom: 32, lineHeight: 1.5, maxWidth: 520 }}>
                {analyst.name} reads your roster automatically. What it can't see — that's this.
              </p>

              {(() => {
                if (leagueGroups.length === 0) return null;
                const isMulti = leagueGroups.length > 1;
                const idx = Math.min(activeLeagueIdx, leagueGroups.length - 1);
                const onLastLeague = idx >= leagueGroups.length - 1;
                const g = leagueGroups[idx];
                const lc = leagueContext[g.key] || {};
                const optionalsExpanded = !!openOptionals[g.key];
                const toggleOptionals = () => setOpenOptionals((p) => ({ ...p, [g.key]: !p[g.key] }));
                const advanceLeague = () => {
                  if (!lc.timeline) { setContextError('Pick where this team is in its cycle.'); window.scrollTo(0, 0); return; }
                  setContextError(null); setActiveLeagueIdx(idx + 1); window.scrollTo(0, 0);
                };
                return (
                  <>
                    {isMulti && <span className="ft-k" style={{ display: 'block', marginBottom: 14 }}>League {idx + 1} of {leagueGroups.length}</span>}
                    <div style={{ position: 'relative', marginBottom: 16, background: 'rgba(180,140,255,0.05)', border: '1px solid rgba(180,140,255,0.15)', borderRadius: 20, padding: '22px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                        {g.avatar ? (
                          <img src={sleeperAvatarSrc(g.avatar)} alt="" onError={(e) => { e.target.style.display = 'none'; }} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', background: 'rgba(180,140,255,0.16)', flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(180,140,255,0.16)', color: '#6A3D9A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, flexShrink: 0 }}>
                            {(g.label || 'Y').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <span className="ft-k" style={{ color: 'rgba(140,100,200,0.8)', display: 'block', marginBottom: 2 }}>Your situation</span>
                          <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--ft-ink)', letterSpacing: '-0.015em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{g.label || 'Your dynasty team'}</span>
                        </div>
                      </div>

                      <div style={{ marginBottom: optionalsExpanded ? 22 : 8 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ft-ink)', letterSpacing: '-0.005em' }}>Where is this team in its cycle?</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ft-red)', letterSpacing: '1.2px', textTransform: 'uppercase' }}>required</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {TIMELINE_OPTIONS.map((opt) => (
                            <ContextChip key={opt} selected={lc.timeline === opt} onClick={() => setLeagueCtx(g.key, 'timeline', opt)}>{opt}</ContextChip>
                          ))}
                        </div>
                      </div>

                      {optionalsExpanded ? (
                        <>
                          <div style={{ height: 1, background: 'var(--ft-hairline)', marginBottom: 22 }} />
                          <div style={{ marginBottom: 18 }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                              <span style={fieldLabelStyle}>How do you build?</span>
                              <span style={{ fontSize: 11, color: 'var(--ft-ink-3)' }}>optional</span>
                            </div>
                            <textarea value={lc.philosophy || ''} onChange={(e) => setLeagueCtx(g.key, 'philosophy', e.target.value)}
                              placeholder="I contend every year and never tank · I hoard 1sts · zero-RB, draft picks over aging vets"
                              maxLength={300}
                              style={{ width: '100%', minHeight: 64, background: 'var(--ft-bg)', border: '1px solid var(--ft-hairline)', borderLeft: '2px solid rgba(180,140,255,0.35)', borderRadius: 10, padding: '12px 14px', fontFamily: ff, fontSize: 14, color: 'var(--ft-ink)', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5 }}
                            />
                          </div>
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                              <span style={fieldLabelStyle}>{product === 'roster' ? 'Anything about the league?' : 'Anything about the league or the other manager?'}</span>
                              <span style={{ fontSize: 11, color: 'var(--ft-ink-3)' }}>optional</span>
                            </div>
                            <textarea value={lc.dynamics || ''} onChange={(e) => setLeagueCtx(g.key, 'dynamics', e.target.value)}
                              placeholder={product === 'roster'
                                ? '12-team SF, active traders · half the league is tanking · nobody answers trade offers until the deadline'
                                : "12-team SF, active traders · he's my biggest rival, don't want to hand him a title · he's checked out and dumping"}
                              maxLength={300}
                              style={{ width: '100%', minHeight: 64, background: 'var(--ft-bg)', border: '1px solid var(--ft-hairline)', borderLeft: '2px solid rgba(180,140,255,0.35)', borderRadius: 10, padding: '12px 14px', fontFamily: ff, fontSize: 14, color: 'var(--ft-ink)', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5 }}
                            />
                          </div>
                          <button onClick={toggleOptionals} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ft-ink-3)', fontFamily: ff, fontSize: 12, marginTop: 12, padding: '4px 0' }}>Hide optional fields</button>
                        </>
                      ) : (
                        <button onClick={toggleOptionals} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(140,100,200,0.9)', fontFamily: ff, fontSize: 13, fontWeight: 600, marginTop: 12, padding: '6px 0', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          + Add more context (optional)
                          <span style={{ fontSize: 11, color: 'var(--ft-ink-3)', fontWeight: 400 }}>· build style, league dynamics</span>
                        </button>
                      )}
                    </div>

                    {isMulti && (
                      <>
                        {contextError && !onLastLeague && <p style={{ color: 'var(--ft-red)', fontSize: 13, marginBottom: 12, marginTop: 4 }}>{contextError}</p>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          {idx > 0 && (
                            <button onClick={() => { setActiveLeagueIdx(idx - 1); setContextError(null); window.scrollTo(0, 0); }}
                              style={{ background: 'transparent', border: '1px solid var(--ft-hairline)', color: 'var(--ft-ink-2)', fontFamily: ff, fontSize: 14, fontWeight: 500, padding: '12px 20px', borderRadius: 'var(--ft-r-pill)', cursor: 'pointer' }}>
                              ← Previous league
                            </button>
                          )}
                          {!onLastLeague && (
                            <button onClick={advanceLeague} className="ft-btn" style={{ flex: 1, minWidth: 200 }}>Next league →</button>
                          )}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}

              {/* Per-item context + final CTA */}
              {(leagueGroups.length === 0 || activeLeagueIdx >= leagueGroups.length - 1) && (
                <>
                  <span className="ft-k" style={{ display: 'block', marginTop: 32, marginBottom: 14 }}>
                    About each {items.length === 1 ? 'item' : `item (${items.length})`}
                  </span>

                  {items.map((item, idx) => {
                    const ic = itemContext[idx] || {};
                    const isExecuted = !!item._sleeperTradeId;
                    const badgeLabel = isExecuted ? 'Trade' : itemTypeBadge(item.type);
                    const ContextChip = ({ selected, onClick, children }) => (
                      <button onClick={onClick} style={{ padding: '9px 16px', background: selected ? 'var(--ft-dark)' : 'transparent', border: `1.5px solid ${selected ? 'var(--ft-dark)' : 'var(--ft-hairline)'}`, borderRadius: 'var(--ft-r-pill)', fontSize: 13, fontWeight: selected ? 600 : 400, color: selected ? '#fff' : 'var(--ft-ink-2)', cursor: 'pointer', transition: 'all 0.15s ease', fontFamily: ff }}>{children}</button>
                    );
                    return (
                      <div key={idx} style={{ marginBottom: 32, paddingBottom: 32, borderBottom: idx < items.length - 1 ? '1px solid var(--ft-hairline)' : 'none' }}>
                        {/* Item summary */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--ft-dark)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{idx + 1}</div>
                          <div style={{ minWidth: 0 }}>
                            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--ft-ink)', lineHeight: 1.3 }}>{itemLabel(item)}</span>
                          </div>
                        </div>

                        {item.type !== 'question' && item.type !== 'roster' && (
                          <>
                            <div style={{ marginBottom: 20 }}>
                              <span className="ft-k" style={{ display: 'block', marginBottom: 12 }}>Who initiated</span>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {['I offered it', 'They offered it', 'Just exploring'].map((opt) => (
                                  <ContextChip key={opt} selected={ic.proposer === opt} onClick={() => setItemCtx(idx, 'proposer', opt)}>{opt}</ContextChip>
                                ))}
                              </div>
                            </div>
                            <div style={{ marginBottom: 20 }}>
                              <span className="ft-k" style={{ display: 'block', marginBottom: 12 }}>What you want</span>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {(isExecuted ? EXECUTED_ASK_OPTIONS : THE_ASK_OPTIONS).map((opt) => (
                                  <ContextChip key={opt} selected={ic.theAsk === opt} onClick={() => setItemCtx(idx, 'theAsk', opt)}>{opt}</ContextChip>
                                ))}
                              </div>
                              {ic.theAsk === 'Something else' && (
                                <textarea
                                  value={ic.theAskOther || ''} onChange={(e) => setItemCtx(idx, 'theAskOther', e.target.value)}
                                  placeholder={`What should ${analyst.name} focus on?`}
                                  style={{ marginTop: 12, width: '100%', minHeight: 56, resize: 'none', fontFamily: ff, fontSize: 14, lineHeight: 1.5, color: 'var(--ft-ink)', background: 'var(--ft-surface)', border: '1px solid var(--ft-hairline)', borderRadius: 12, padding: '12px 14px', outline: 'none', boxSizing: 'border-box' }}
                                />
                              )}
                            </div>
                          </>
                        )}

                        <div>
                          <span className="ft-k" style={{ display: 'block', marginBottom: 12 }}>
                            {item.type === 'question' ? 'Context' : item.type === 'roster' ? 'Focus question' : 'Gut feeling'} <span style={{ color: 'var(--ft-ink-3)', fontWeight: 400, letterSpacing: 0, textTransform: 'none', fontSize: 11 }}>optional</span>
                          </span>
                          <textarea
                            value={ic.worry || ''} onChange={(e) => setItemCtx(idx, 'worry', e.target.value)}
                            placeholder={
                              item.type === 'question' ? 'Backstory, what you have already considered…'
                              : item.type === 'roster' ? 'One thing you want answered — is my window real, who do I move first, what are my picks for…'
                              : 'Selling low? Feel like you’re getting fleeced? Say it.'
                            }
                            maxLength={400}
                            style={{ width: '100%', minHeight: 64, background: 'var(--ft-surface)', border: '1px solid var(--ft-hairline)', borderRadius: 12, padding: '14px 16px', fontFamily: ff, fontSize: 14, color: 'var(--ft-ink)', resize: 'none', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5 }}
                          />
                        </div>
                      </div>
                    );
                  })}

                  {(contextError || paymentError) && (
                    <p style={{ color: 'var(--ft-red)', fontSize: 13, marginBottom: 16, marginTop: 4 }}>{contextError || paymentError}</p>
                  )}

                  <button onClick={handleAdvanceFromContext} disabled={submitting} className="ft-btn" style={{ width: '100%', padding: '17px 40px', fontSize: 16, opacity: submitting ? 0.45 : 1 }}>
                    {submitting ? 'Setting up…' : 'Continue →'}
                  </button>
                </>
              )}
            </div>
          );
        })()}

        {/* ── Auth (email collection — only if not logged in, after context) ── */}
        {step === 'auth' && (
          <div className="ft-card" style={{ background: 'var(--ft-bg)' }}>
            <span className="ft-k" style={{ display: 'block', marginBottom: 16 }}>Almost there</span>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ft-ink)', letterSpacing: '-0.02em', margin: '0 0 8px' }}>
              Where do we send the review?
            </h2>
            <p style={{ fontSize: 15, color: 'var(--ft-ink-2)', marginBottom: 28, lineHeight: 1.5 }}>
              We'll email you as soon as {analyst.name} drops it.
            </p>
            {!magicLinkSent ? (
              <form onSubmit={handleSendMagicLink}>
                <input
                  type="email" placeholder="your@email.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} required
                  style={{
                    width: '100%', background: 'var(--ft-surface)', border: '1px solid var(--ft-hairline)',
                    borderRadius: 12, padding: '16px 20px', fontSize: 17, fontFamily: 'var(--ft-font)', color: 'var(--ft-ink)',
                    outline: 'none', boxSizing: 'border-box', marginBottom: 16,
                  }}
                />
                <button type="submit" disabled={authLoading || !email} style={{
                  width: '100%', background: 'var(--ft-dark)', color: '#fff', border: 'none',
                  borderRadius: 'var(--ft-r-pill)', padding: '17px 40px', fontSize: 16, fontWeight: 600,
                  letterSpacing: '-0.01em', cursor: !email || authLoading ? 'not-allowed' : 'pointer',
                  opacity: (authLoading || !email) ? 0.35 : 1, fontFamily: ff, transition: 'opacity 0.2s',
                }}>
                  {authLoading ? 'Sending…' : 'Continue →'}
                </button>
              </form>
            ) : (
              <div style={{ padding: '16px 0' }}>
                <h3 style={{ fontSize: 17, fontWeight: 600, color: 'var(--ft-ink)', margin: '0 0 8px' }}>Check your email.</h3>
                <p style={{ fontSize: 15, color: 'var(--ft-ink-3)', margin: '0 0 16px' }}>Sent a link to {email}. Click it to continue — we'll pick right back up here.</p>
                <p style={{ fontSize: 13, color: 'var(--ft-ink-3)' }}>Your items are saved. This page will be waiting for you.</p>
              </div>
            )}
          </div>
        )}

        {/* ── Sleeper (link username — only if logged in but no sleeper yet, after context) ── */}
        {step === 'sleeper' && (
          <div className="ft-card" style={{ background: 'var(--ft-bg)' }}>
            <span className="ft-k" style={{ display: 'block', marginBottom: 16 }}>One more thing</span>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ft-ink)', letterSpacing: '-0.02em', margin: '0 0 8px' }}>
              What's your Sleeper username?
            </h2>
            <p style={{ fontSize: 15, color: 'var(--ft-ink-2)', marginBottom: 28, lineHeight: 1.5 }}>
              {analyst.name} uses your roster, league settings, and history to give a take that fits your exact situation. We'll only ask once.
            </p>
            <form onSubmit={handleLinkSleeper}>
              <input
                placeholder="Sleeper username"
                value={sleeperInput}
                onChange={(e) => { setSleeperInput(e.target.value); setSleeperError(null); }}
                autoCapitalize="none" autoCorrect="off" spellCheck={false} required
                style={{
                  width: '100%', background: 'var(--ft-surface)', border: '1px solid var(--ft-hairline)',
                  borderRadius: 12, padding: '16px 20px', fontSize: 17, fontFamily: 'var(--ft-font)', color: 'var(--ft-ink)',
                  outline: 'none', boxSizing: 'border-box', marginBottom: 16,
                }}
              />
              {sleeperError && <p style={{ color: 'var(--ft-red)', fontSize: 13, marginBottom: 16 }}>{sleeperError}</p>}
              <button type="submit" disabled={sleeperLoading || !sleeperInput.trim()} style={{
                width: '100%', background: 'var(--ft-dark)', color: '#fff', border: 'none',
                borderRadius: 'var(--ft-r-pill)', padding: '17px 40px', fontSize: 16, fontWeight: 600,
                letterSpacing: '-0.01em', cursor: !sleeperInput.trim() || sleeperLoading ? 'not-allowed' : 'pointer',
                opacity: (sleeperLoading || !sleeperInput.trim()) ? 0.35 : 1, fontFamily: ff, transition: 'opacity 0.2s',
              }}>
                {sleeperLoading ? 'Linking…' : 'Continue →'}
              </button>
            </form>
          </div>
        )}

        {/* ── Payment loading (creating PI after auth+sleeper gates). On failure,
            surface the error with a retry — the spinner alone strands the user. ── */}
        {(step === 'payment_loading') && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '60px 0' }}>
            {paymentError ? (
              <>
                <p style={{ color: 'var(--ft-red)', fontSize: 14, margin: 0, textAlign: 'center' }}>{paymentError}</p>
                <button
                  className="ft-btn sm"
                  onClick={() => { setPaymentError(null); handleProceedToPayment(); }}
                >
                  Try again
                </button>
              </>
            ) : (
              <span style={{ color: 'var(--ft-ink-3)', fontSize: 15 }}>Setting up payment…</span>
            )}
          </div>
        )}

        {/* ── Payment loading ── */}
        {step === 'payment' && !clientSecret && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <span style={{ color: 'var(--ft-ink-3)', fontSize: 15 }}>Setting up payment…</span>
          </div>
        )}

        {/* ── Payment ── */}
        {step === 'payment' && clientSecret && (
          <div className="ft-card">
            <span className="ft-k" style={{ display: 'block', marginBottom: 16 }}>Step 3 of 3</span>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ft-ink)', letterSpacing: '-0.02em', margin: '0 0 8px' }}>
              Pay <Price analyst={analyst} digits={2} product={product} /> and submit.
            </h2>
            <p style={{ fontSize: 15, color: 'var(--ft-ink-2)', marginBottom: 28 }}>
              {analyst.slaHours}h SLA. Full refund if missed.
            </p>

            {items.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ft-disabled)', flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: 'var(--ft-ink-3)' }}>{itemLabel(item)}</span>
              </div>
            ))}

            <div style={{ height: 1, background: 'var(--ft-hairline)', margin: '24px 0' }} />

            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#1D1D1F', colorBackground: '#FFFFFF',
                    colorText: '#1D1D1F', colorDanger: '#FF3B30',
                    borderRadius: '10px', fontFamily: "'-apple-system', 'BlinkMacSystemFont', 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
                  },
                  rules: {
                    '.Input': { border: '1px solid #D2D2D7', boxShadow: 'none', backgroundColor: '#F5F5F7' },
                    '.Input:focus': { border: '1px solid #1D1D1F', boxShadow: 'none', backgroundColor: '#FFFFFF' },
                  },
                },
              }}
            >
              <CheckoutForm analyst={analyst} reviewId={reviewId} onSuccess={() => {
                        try {
                          ['items','lctx','ictx'].forEach(k => sessionStorage.removeItem(`ft_ar_${k}_${slug}`));
                          sessionStorage.removeItem(`ft_ar_su_${slug}`);
                          localStorage.removeItem(`ft_ar_snap_${slug}`);
                        } catch {}
                        setStep('success');
                      }} />
            </Elements>
          </div>
        )}

        {/* ── Success ── */}
        {step === 'success' && (
          <div style={{ paddingTop: 24 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--ft-green-soft)', border: '1px solid rgba(52,199,89,0.3)', borderRadius: 'var(--ft-r-pill)', padding: '6px 14px', marginBottom: 24 }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--ft-green)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>✓</div>
              <span className="ft-k" style={{ color: 'var(--ft-green-ink)' }}>Payment received</span>
            </div>

            <h1 className="ft-hero" style={{ marginBottom: 16 }}>Review submitted.</h1>
            <p className="ft-sub" style={{ marginBottom: 32 }}>
              {analyst.name} will respond within {analyst.slaHours}h. We'll email you the moment it lands.
            </p>

            <div style={{ background: 'rgba(180,140,255,0.06)', border: '1px solid rgba(180,140,255,0.18)', borderRadius: 18, padding: '20px 24px', marginBottom: 36 }}>
              <span className="ft-k" style={{ color: 'rgba(140,100,200,0.85)', display: 'block', marginBottom: 16 }}>What happens next</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { n: 1, t: `${analyst.name} gets your submission`, s: 'Notified now. Your roster, league, and context are already attached.' },
                  { n: 2, t: 'Reviewed and graded', s: `Per-item written take plus a structured verdict. Usually well inside the ${analyst.slaHours}h window.` },
                  { n: 3, t: 'You get an email', s: 'Link straight to the verdict. If the window is missed, you get a full refund automatically.' },
                ].map((row) => (
                  <div key={row.n} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(180,140,255,0.15)', color: '#6A3D9A', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{row.n}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ft-ink)', letterSpacing: '-0.005em' }}>{row.t}</div>
                      <div style={{ fontSize: 13, color: 'var(--ft-ink-2)', lineHeight: 1.5, marginTop: 2 }}>{row.s}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => { setStep('profile'); setItems([]); setLeagueContext({}); setItemContext({}); setReviewId(null); setClientSecret(null); window.scrollTo(0, 0); }}
                className="ft-btn"
                style={{ width: '100%', padding: '16px 20px', textAlign: 'left', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <span>Submit another review</span>
                <span style={{ opacity: 0.5 }}>→</span>
              </button>
            </div>

            {/* While they wait — the two products that keep them coming back daily */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 28 }}>
              <span className="ft-k" style={{ display: 'block', marginBottom: 2 }}>While you wait</span>
              <UpsellCard
                wordmark="The Curve"
                icon={CurveIcon}
                title="Play today's Curve."
                body="Draw your line on a player's value before it moves. A new one drops every morning."
                cta="Play the Curve"
                to="/curve"
              />
              <UpsellCard
                wordmark="Manager Intel"
                icon={IntelIcon}
                title="Scout your leaguemates."
                body="Trade history, tendencies, titles — the full read on anyone in your league."
                cta={user?.sleeperUsername ? 'See your intel' : 'Scout a manager'}
                to={user?.sleeperUsername ? `/manager-intel/${user.sleeperUsername}` : '/intel'}
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AnalystLanding;
